import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSettingsStore, DEFAULT_WHOLE_CANDLE_SETTINGS, DEFAULT_PINNING_VOLUME_SETTINGS, DEFAULT_DYNAMIC_THRESHOLDS, DEFAULT_TRAILING_CANDLE_SETTINGS, DEFAULT_TRAILING_PULLBACK_SETTINGS, DEFAULT_MAGNET_CANDLE_SETTINGS, TrailingCandleSettings, MagnetCandleSettings, normalizeToBaseSymbol, SRProximityAlertSettings, DEFAULT_SR_PROXIMITY_ALERT_SETTINGS, BreakOfStructureSettings, DEFAULT_BOS_SETTINGS } from '../store/settingsStore';
import { useVolumeProfileStore } from '../store/volumeProfileStore';
import { VolumeProfileSettings, DEFAULT_VOLUME_PROFILE_SETTINGS, SessionType } from '../types/volumeProfile';
import { ICTSettings, ICTTimeframe, DEFAULT_ICT_SETTINGS, TrailingPullbackSettings } from '@shared/types';
import { useThemeStore } from '../store/themeStore';
import { useSymbolStore } from '../store/symbolStore';
import { useMarketStore } from '../store/marketStore';
import { useDonchianSettingsStore } from '../store/donchianSettingsStore';
import { themes } from '../config/themes';
import { CONFIG } from '../config';
import { wsService } from '../services/WebSocketService';
import { strategyService } from '../services/StrategyService';
import { logout } from './PasscodeGate';
import ATROptimizationModal from './ATROptimizationModal';
import SymbolManagementPanel from './SymbolManagementPanel';
import { useDefaultQuantity } from '../hooks/useDefaultQuantity';
import { MAX_ORDER_QUANTITY, MIN_ORDER_QUANTITY, QUANTITY_PRESETS } from '../config/orderQuantity';
import { VolumeMarkersSettingsPanel } from './VolumeMarkersSettings';
import { useVestSpikeSettingsStore } from '../store/vestSpikeSettingsStore';
import { useVolumePaneSettingsStore } from '../store/volumePaneSettingsStore';
import { DEFAULT_CME_SPIKE_SETTINGS } from '../utils/cmeVolumeSpikes';
import { WickRejectionSettingsPanel } from './WickRejectionSettings';
import { useWickRejectionSettingsStore } from '../store/wickRejectionSettingsStore';
import { useLevelsStore } from '../store/levelsStore';
import './GlobalSettingsModal.css';
import { isVestFeedSymbol } from '../utils/vestFeed';
import SymbolSettingsPanel from './SymbolSettingsPanel';

interface GlobalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: 'general' | 'strategy';
  initialTab?: string;
  /** Current chart symbol - used to auto-select the matching symbol tab */
  initialSymbol?: string;
}

/**
 * Extract base symbol from a full symbol string using dynamic symbol registry
 * e.g., "MNQH6.CME" -> "MNQ", "ESM5" -> "ES", "SILH6.COMEX" -> "SIL"
 */
function extractBaseSymbol(symbol: string | undefined): string | null {
  if (!symbol) return null;
  // A Vest chart feed symbol has its own settings document (SymbolSettingsPanel),
  // never a tab of the CME per-symbol settings below.
  if (isVestFeedSymbol(symbol)) return null;
  return normalizeToBaseSymbol(symbol);
}

/**
 * Check if a symbol is in the symbol registry
 */
function isRegisteredSymbol(symbol: string): boolean {
  const registrySymbols = useSymbolStore.getState().symbols.map(s => s.base.toUpperCase());
  return registrySymbols.includes(symbol.toUpperCase());
}

type SidebarSection = 'general' | 'strategy' | 'symbols';
type GeneralTab = 'appearance' | 'notifications' | 'trading' | 'account';
type StrategyTab = 'whole-candle' | 'pinning-volume' | 'ict' | 'volume-profile' | 'donchian' | 'break-of-structure' | 'trailing-pullback' | 'magnet-candle' | 'sr-alerts' | 'volume-markers' | 'wick-rejection';

// Get enabled symbols from registry (dynamic)
function getEnabledSymbols(): string[] {
  return useSymbolStore.getState().symbols.filter(s => s.enabled && !isVestFeedSymbol(s.base)).map(s => s.base);
}
// SymbolTab can be any string (dynamic symbols)
type SymbolTab = string;

interface ATRThresholdMapping {
  atrMin: number;
  atrMax: number;
  volumeMultiplier: number;
  minVolumeRatio: number;
  label: string;
}

interface DynamicThresholdConfig {
  enabled: boolean;
  mappings: ATRThresholdMapping[];
  fallbackVolumeMultiplier: number;
  fallbackMinVolumeRatio: number;
}

interface ActiveThresholds {
  currentATR: number;
  atrLabel: string | null;
  volumeMultiplier: number;
  minVolumeRatio: number;
  dynamicEnabled: boolean;
}

interface PinningVolumeSettings {
  enabled: boolean;
  volumeMultiplier: number;
  volumeLookback: number;
  minVolumeRatio: number;
  minWickPercentage: number;
  priorMoveLookback: number;
  useWickDirection: boolean;
  requireAlignedWick: boolean;
  skipIfLowATR: boolean;
  atrMinThreshold: number;
  skipIfHOD: boolean;
  skipIfLOD: boolean;
  hodLodRangePoints: number;
  skipIfSmallCandle: boolean;
  minCandleLengthPoints: number;
  minCandleLengthPointsEs?: number;
  tradeExecutionEnabled: boolean;
  dynamicThresholds?: DynamicThresholdConfig;
  confluenceEnabled?: boolean;
}

// Defaults are now imported from settingsStore (single source of truth)
// Use DEFAULT_PINNING_VOLUME_SETTINGS and DEFAULT_DYNAMIC_THRESHOLDS



interface DonchianSettings {
  enabled: boolean;
  entryLookback: number;
  trendLookback: number;
  exitLookback: number;
  volumeMultiplier: number;
  volumeLookback: number;
  requireTrendFilter: boolean;
}

const DEFAULT_DONCHIAN_SETTINGS: DonchianSettings = {
  enabled: true,
  entryLookback: 20,
  trendLookback: 55,
  exitLookback: 10,
  volumeMultiplier: 1.5,
  volumeLookback: 10,
  requireTrendFilter: true,
};

// Helper to format time ago
function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const GlobalSettingsModal: React.FC<GlobalSettingsModalProps> = ({
  isOpen,
  onClose,
  initialSection = 'general',
  initialTab,
  initialSymbol,
}) => {
  // A Vest chart feed chart edits ITS OWN settings document, never NQ's.
  const vestSymbol = initialSymbol && isVestFeedSymbol(initialSymbol) ? initialSymbol.trim().toUpperCase() : null;

  // Extract base symbol and determine initial tab
  const currentBaseSymbol = extractBaseSymbol(initialSymbol);
  const initialSymbolTab = currentBaseSymbol || 'NQ';

  // Build dynamic symbol tabs from registry + current symbol if not already included
  const registeredSymbols = getEnabledSymbols();
  const symbolTabs: string[] = [...registeredSymbols];
  if (currentBaseSymbol && !isRegisteredSymbol(currentBaseSymbol)) {
    symbolTabs.push(currentBaseSymbol);
  }

  // Sidebar & tab state
  const [activeSection, setActiveSection] = useState<SidebarSection>(initialSection);
  const [generalTab, setGeneralTab] = useState<GeneralTab>(
    (initialTab as GeneralTab) || 'appearance'
  );
  const [strategyTab, setStrategyTab] = useState<StrategyTab>(
    (initialTab as StrategyTab) || 'whole-candle'
  );
  const [symbolTab, setSymbolTab] = useState<SymbolTab>(initialSymbolTab);

  // Default order quantity — the same stored preference desktop and mobile
  // order entry read, so this is the only place it needs setting.
  const { defaultQuantity, setDefaultQuantity } = useDefaultQuantity();
  // The input keeps its own draft string so a half-typed value ("" on the way
  // to "12") stays on screen while the stored default refuses to take it.
  const [defaultQtyDraft, setDefaultQtyDraft] = useState<string>(String(defaultQuantity));
  useEffect(() => {
    setDefaultQtyDraft(String(defaultQuantity));
  }, [defaultQuantity]);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Theme
  const { currentTheme, setTheme } = useThemeStore();

  // Whole Candle settings (per-symbol)
  const {
    wholeCandle: globalWCSettings,
    updateWholeCandleSettings: updateSettings,
    setWholeCandleSettings: setSettings,
    updatePinningVolumeSettings: updatePVStoreSettings,
    // Sync status from store
    isSyncing,
    lastSyncAt,
    lastSyncError,
    setSyncing,
    setSyncSuccess,
    setSyncError,
  } = useSettingsStore();

  // Per-symbol WCC settings (dynamically populated from server on modal open)
  const [wccSettingsBySymbol, setWccSettingsBySymbol] = useState<Record<string, typeof globalWCSettings>>({});
  const [originalWccSettingsBySymbol, setOriginalWccSettingsBySymbol] = useState<Record<string, typeof globalWCSettings>>({});

  // Get current symbol's settings (fallback to MNQ defaults for new symbols)
  const localSettings = wccSettingsBySymbol[symbolTab] || { ...DEFAULT_WHOLE_CANDLE_SETTINGS };
  const setLocalSettings = (newSettings: typeof globalWCSettings | ((prev: typeof globalWCSettings) => typeof globalWCSettings)) => {
    setWccSettingsBySymbol(prev => {
      const current = prev[symbolTab] || { ...DEFAULT_WHOLE_CANDLE_SETTINGS };
      return {
        ...prev,
        [symbolTab]: typeof newSettings === 'function' ? newSettings(current) : newSettings
      };
    });
  };

  const [hasChanges, setHasChanges] = useState(false);
  // isSaving is now tracked in Zustand store as isSyncing

  // Per-symbol Pinning Volume settings (dynamically populated from server on modal open)
  const [pvSettingsBySymbol, setPvSettingsBySymbol] = useState<Record<string, PinningVolumeSettings>>({});
  const [originalPvSettingsBySymbol, setOriginalPvSettingsBySymbol] = useState<Record<string, PinningVolumeSettings>>({});

  // Get current symbol's PV settings (fallback to MNQ defaults for new symbols)
  const pinningVolumeSettings = pvSettingsBySymbol[symbolTab] || { ...DEFAULT_PINNING_VOLUME_SETTINGS };
  const setPinningVolumeSettings = (newSettings: PinningVolumeSettings | ((prev: PinningVolumeSettings) => PinningVolumeSettings)) => {
    setPvSettingsBySymbol(prev => {
      const current = prev[symbolTab] || { ...DEFAULT_PINNING_VOLUME_SETTINGS };
      return {
        ...prev,
        [symbolTab]: typeof newSettings === 'function' ? newSettings(current) : newSettings
      };
    });
  };

  const [pinningVolumeHasChanges, setPinningVolumeHasChanges] = useState(false);
  const [activeThresholds, setActiveThresholds] = useState<ActiveThresholds | null>(null);

  // Global ICT settings (not per-symbol)
  const [ictSettings, setIctSettings] = useState<ICTSettings>({ ...DEFAULT_ICT_SETTINGS });
  const [originalIctSettings, setOriginalIctSettings] = useState<ICTSettings>({ ...DEFAULT_ICT_SETTINGS });

  const [ictHasChanges, setIctHasChanges] = useState(false);

  // Trailing candle settings (global, not per-symbol)
  const [trailingSettings, setTrailingSettings] = useState<TrailingCandleSettings>(DEFAULT_TRAILING_CANDLE_SETTINGS);
  const [originalTrailingSettings, setOriginalTrailingSettings] = useState<TrailingCandleSettings>(DEFAULT_TRAILING_CANDLE_SETTINGS);
  const [trailingHasChanges, setTrailingHasChanges] = useState(false);

  // Trailing pullback strategy settings (global)
  const [trailingPullbackSettings, setTrailingPullbackSettings] = useState<TrailingPullbackSettings>(DEFAULT_TRAILING_PULLBACK_SETTINGS);
  const [originalTrailingPullbackSettings, setOriginalTrailingPullbackSettings] = useState<TrailingPullbackSettings>(DEFAULT_TRAILING_PULLBACK_SETTINGS);
  const [trailingPullbackHasChanges, setTrailingPullbackHasChanges] = useState(false);


  // Magnet candle strategy settings (per-symbol, like WCC/PV)
  const [magnetCandleSettings, setMagnetCandleSettings] = useState<MagnetCandleSettings>(DEFAULT_MAGNET_CANDLE_SETTINGS);
  const [originalMagnetCandleSettings, setOriginalMagnetCandleSettings] = useState<MagnetCandleSettings>(DEFAULT_MAGNET_CANDLE_SETTINGS);
  const [magnetCandleHasChanges, setMagnetCandleHasChanges] = useState(false);
  const [mcSettingsBySymbol, setMcSettingsBySymbol] = useState<Record<string, MagnetCandleSettings>>({});
  const [originalMcSettingsBySymbol, setOriginalMcSettingsBySymbol] = useState<Record<string, MagnetCandleSettings>>({});

  // SR Proximity Alert settings (global)
  const [srAlertSettings, setSrAlertSettings] = useState<SRProximityAlertSettings>(DEFAULT_SR_PROXIMITY_ALERT_SETTINGS);
  const [srAlertHasChanges, setSrAlertHasChanges] = useState(false);

  // Volume Profile settings (global)
  const [vpSettings, setVpSettings] = useState<VolumeProfileSettings>(
    useVolumeProfileStore.getState().settings
  );
  const [originalVpSettings, setOriginalVpSettings] = useState<VolumeProfileSettings>(
    useVolumeProfileStore.getState().settings
  );
  const [vpHasChanges, setVpHasChanges] = useState(false);

  // Donchian Channels settings (global)
  const [donchianSettings, setDonchianSettings] = useState<DonchianSettings>(DEFAULT_DONCHIAN_SETTINGS);
  const [originalDonchianSettings, setOriginalDonchianSettings] = useState<DonchianSettings>(DEFAULT_DONCHIAN_SETTINGS);
  const [donchianHasChanges, setDonchianHasChanges] = useState(false);

  // Break of Structure (BOS) settings (global)
  const [bosSettings, setBosSettings] = useState<BreakOfStructureSettings>(DEFAULT_BOS_SETTINGS);
  const [originalBosSettings, setOriginalBosSettings] = useState<BreakOfStructureSettings>(DEFAULT_BOS_SETTINGS);
  const [bosHasChanges, setBosHasChanges] = useState(false);

  // Position alert settings
  const [posAlertSettings, setPosAlertSettings] = useState({
    positionOpened: true,
    positionClosed: true,
    drawdownEnabled: true,
    drawdownThresholdPts: 30,
    tpApproachingEnabled: true,
    tpApproachPct: 90,
  });
  const [originalPosAlertSettings, setOriginalPosAlertSettings] = useState(posAlertSettings);
  const [posAlertHasChanges, setPosAlertHasChanges] = useState(false);

  // Recalculate state
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Optimization modal
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);

  // Logout state
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // logout() reloads the page on success, so we don't need to do anything else
    } catch (err) {
      console.error('Logout failed:', err);
      setIsLoggingOut(false);
    }
  };

  // Update symbol tab when modal opens with a different symbol
  useEffect(() => {
    if (isOpen && initialSymbol) {
      const tab = extractBaseSymbol(initialSymbol);
      if (tab) {
        setSymbolTab(tab);
      }
    }
  }, [isOpen, initialSymbol]);

  // Fetch per-symbol settings when modal opens
  useEffect(() => {
    if (isOpen) {
      // Fetch settings for each symbol in tabs (including dynamic ones)
      symbolTabs.forEach(sym => {
        wsService.emit('get-strategy-settings-for-symbol', { symbol: sym });
        wsService.emit('get-pinning-volume-settings-for-symbol', { symbol: sym });
      });
      // ICT settings are global — fetch once
      wsService.emit('get-ict-settings');
      wsService.emit('get-pinning-volume-active-thresholds');

      // Handler for per-symbol WCC settings (accepts any symbol)
      const handleWCSettingsForSymbol = (data: { symbol: string; settings: any }) => {
        const sym = data.symbol.toUpperCase();

        const serverSettings = data.settings;
        const merged = serverSettings
          ? { ...DEFAULT_WHOLE_CANDLE_SETTINGS, ...serverSettings }
          : { ...DEFAULT_WHOLE_CANDLE_SETTINGS };

        setWccSettingsBySymbol(prev => ({ ...prev, [sym]: merged }));
        setOriginalWccSettingsBySymbol(prev => ({ ...prev, [sym]: merged }));

        // Sync to Zustand store for ChartView to pick up
        useSettingsStore.getState().setWholeCandleForSymbol(sym, merged);

        // Also update global store for NQ (primary symbol)
        if (sym === 'NQ') {
          setSettings(merged);
        }
      };

      // Handler for per-symbol PV settings (accepts any symbol)
      const handlePVSettingsForSymbol = (data: { symbol: string; settings: PinningVolumeSettings }) => {
        const sym = data.symbol.toUpperCase();

        const serverSettings = data.settings;
        const merged = serverSettings
          ? {
              ...DEFAULT_PINNING_VOLUME_SETTINGS,
              ...serverSettings,
              dynamicThresholds: {
                ...DEFAULT_DYNAMIC_THRESHOLDS,
                ...serverSettings.dynamicThresholds,
              },
            }
          : DEFAULT_PINNING_VOLUME_SETTINGS;

        setPvSettingsBySymbol(prev => ({ ...prev, [sym]: merged }));
        setOriginalPvSettingsBySymbol(prev => ({ ...prev, [sym]: merged }));

        // Sync to Zustand store for ChartView to pick up
        useSettingsStore.getState().setPinningVolumeForSymbol(sym, merged);

        // Also update global store for NQ (primary symbol)
        if (sym === 'NQ') {
          updatePVStoreSettings({ enabled: merged.enabled });
        }
      };

      const handleActiveThresholds = (thresholds: ActiveThresholds | null) => {
        setActiveThresholds(thresholds);
      };

      // Handler for global ICT settings
      const handleICTSettings = (serverSettings: ICTSettings) => {
        const merged = serverSettings
          ? { ...DEFAULT_ICT_SETTINGS, ...serverSettings }
          : { ...DEFAULT_ICT_SETTINGS };

        setIctSettings(merged);
        setOriginalIctSettings(merged);
        useSettingsStore.getState().setICTSettings(merged);
      };

      wsService.on('strategy-settings-for-symbol', handleWCSettingsForSymbol);
      wsService.on('pinning-volume-settings-for-symbol', handlePVSettingsForSymbol);
      wsService.on('pinning-volume-active-thresholds', handleActiveThresholds);
      wsService.on('ict-settings', handleICTSettings);

      // Fetch trailing candle settings (global, via REST API)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-candle-settings`)
        .then(res => res.json())
        .then(data => {
          const settings: TrailingCandleSettings = {
            enabled: data.enabled ?? DEFAULT_TRAILING_CANDLE_SETTINGS.enabled,
            durationSeconds: data.duration_seconds ?? DEFAULT_TRAILING_CANDLE_SETTINGS.durationSeconds,
          };
          setTrailingSettings(settings);
          setOriginalTrailingSettings(settings);
        })
        .catch(err => console.error('Failed to load trailing candle settings:', err));

      // Fetch trailing pullback strategy settings (global, via REST API)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`)
        .then(res => res.json())
        .then(data => {
          const settings: TrailingPullbackSettings = {
            enabled: data.enabled ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.enabled,
            confidenceThreshold: data.confidenceThreshold ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.confidenceThreshold,
            includeContinuation: data.includeContinuation ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.includeContinuation,
            requireWccAlignment: data.requireWccAlignment ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.requireWccAlignment,
            minTrailingBodyPct: data.minTrailingBodyPct ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.minTrailingBodyPct,
            updatedAt: data.updatedAt ?? Date.now(),
          };
          setTrailingPullbackSettings(settings);
          setOriginalTrailingPullbackSettings(settings);
        })
        .catch(err => console.error('Failed to load trailing pullback settings:', err));

      // Fetch magnet candle settings (global, via REST API)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings`)
        .then(res => res.json())
        .then(data => {
          const settings: MagnetCandleSettings = {
            enabled: data.enabled ?? DEFAULT_MAGNET_CANDLE_SETTINGS.enabled,
            wickThreshold: data.wickThreshold ?? DEFAULT_MAGNET_CANDLE_SETTINGS.wickThreshold,
            minBodyPct: data.minBodyPct ?? DEFAULT_MAGNET_CANDLE_SETTINGS.minBodyPct,
            minRangePoints: data.minRangePoints ?? DEFAULT_MAGNET_CANDLE_SETTINGS.minRangePoints,
            maxActiveMagnets: data.maxActiveMagnets ?? DEFAULT_MAGNET_CANDLE_SETTINGS.maxActiveMagnets,
            lookbackCandles: data.lookbackCandles ?? DEFAULT_MAGNET_CANDLE_SETTINGS.lookbackCandles,
            extensionLineCount: data.extensionLineCount ?? DEFAULT_MAGNET_CANDLE_SETTINGS.extensionLineCount,
            updatedAt: data.updatedAt ?? 0,
          };
          setMagnetCandleSettings(settings);
          setOriginalMagnetCandleSettings(settings);
          useSettingsStore.getState().setMagnetCandleSettings(settings);
        })
        .catch(err => console.error('Failed to load magnet candle settings:', err));

      // Fetch per-symbol magnet candle settings
      symbolTabs.forEach(sym => {
        fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings/${sym}`)
          .then(res => res.json())
          .then(data => {
            const settings: MagnetCandleSettings = {
              enabled: data.enabled ?? DEFAULT_MAGNET_CANDLE_SETTINGS.enabled,
              wickThreshold: data.wickThreshold ?? DEFAULT_MAGNET_CANDLE_SETTINGS.wickThreshold,
              minBodyPct: data.minBodyPct ?? DEFAULT_MAGNET_CANDLE_SETTINGS.minBodyPct,
              minRangePoints: data.minRangePoints ?? DEFAULT_MAGNET_CANDLE_SETTINGS.minRangePoints,
              maxActiveMagnets: data.maxActiveMagnets ?? DEFAULT_MAGNET_CANDLE_SETTINGS.maxActiveMagnets,
              lookbackCandles: data.lookbackCandles ?? DEFAULT_MAGNET_CANDLE_SETTINGS.lookbackCandles,
              extensionLineCount: data.extensionLineCount ?? DEFAULT_MAGNET_CANDLE_SETTINGS.extensionLineCount,
              updatedAt: data.updatedAt ?? 0,
            };
            setMcSettingsBySymbol(prev => ({ ...prev, [sym]: settings }));
            setOriginalMcSettingsBySymbol(prev => ({ ...prev, [sym]: settings }));
            useSettingsStore.getState().setMagnetCandleForSymbol(sym, settings);
          })
          .catch(err => console.error(`Failed to load magnet candle settings for ${sym}:`, err));
      });

      // Fetch SR proximity alert settings (global)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/sr-proximity-settings`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            const settings: SRProximityAlertSettings = { ...DEFAULT_SR_PROXIMITY_ALERT_SETTINGS, ...data };
            setSrAlertSettings(settings);
            useSettingsStore.getState().setSRProximityAlertSettings(settings);
          }
        })
        .catch(err => console.warn('[GSM] Could not load SR alert settings:', err));

      // Fetch Donchian Channels settings (global, via REST API)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/donchian-settings`)
        .then(res => res.json())
        .then(data => {
          const settings: DonchianSettings = {
            enabled: data.enabled ?? DEFAULT_DONCHIAN_SETTINGS.enabled,
            entryLookback: data.entryLookback ?? DEFAULT_DONCHIAN_SETTINGS.entryLookback,
            trendLookback: data.trendLookback ?? DEFAULT_DONCHIAN_SETTINGS.trendLookback,
            exitLookback: data.exitLookback ?? DEFAULT_DONCHIAN_SETTINGS.exitLookback,
            volumeMultiplier: data.volumeMultiplier ?? DEFAULT_DONCHIAN_SETTINGS.volumeMultiplier,
            volumeLookback: data.volumeLookback ?? DEFAULT_DONCHIAN_SETTINGS.volumeLookback,
            requireTrendFilter: data.requireTrendFilter ?? DEFAULT_DONCHIAN_SETTINGS.requireTrendFilter,
          };
          setDonchianSettings(settings);
          setOriginalDonchianSettings(settings);
          useDonchianSettingsStore.getState().setSettings(settings);
        })
        .catch(err => console.error('Failed to load donchian settings:', err));

      // Fetch Break of Structure (BOS) settings (global, via REST API)
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-settings`)
        .then(res => res.json())
        .then(data => {
          const settings: BreakOfStructureSettings = { ...DEFAULT_BOS_SETTINGS, ...data };
          setBosSettings(settings);
          setOriginalBosSettings(settings);
          useSettingsStore.getState().setBosSettings(settings);
        })
        .catch(err => console.error('Failed to load BOS settings:', err));

      // Fetch volume profile settings (global, via REST API)
      useVolumeProfileStore.getState().loadSettings().then(() => {
        const settings = useVolumeProfileStore.getState().settings;
        setVpSettings(settings);
        setOriginalVpSettings(settings);
      });

      // Fetch position alert settings
      fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/position-alert-settings`)
        .then(res => res.json())
        .then(data => {
          const settings = {
            positionOpened: data.positionOpened ?? true,
            positionClosed: data.positionClosed ?? true,
            drawdownEnabled: data.drawdownEnabled ?? true,
            drawdownThresholdPts: data.drawdownThresholdPts ?? 30,
            tpApproachingEnabled: data.tpApproachingEnabled ?? true,
            tpApproachPct: data.tpApproachPct ?? 90,
          };
          setPosAlertSettings(settings);
          setOriginalPosAlertSettings(settings);
        })
        .catch(err => console.error('Failed to load position alert settings:', err));

      return () => {
        wsService.off('strategy-settings-for-symbol', handleWCSettingsForSymbol);
        wsService.off('pinning-volume-settings-for-symbol', handlePVSettingsForSymbol);
        wsService.off('pinning-volume-active-thresholds', handleActiveThresholds);
        wsService.off('ict-settings', handleICTSettings);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, setSettings, updatePVStoreSettings, symbolTabs.join(',')]);

  // Track changes (per-symbol)
  useEffect(() => {
    const currentOriginal = originalWccSettingsBySymbol[symbolTab];
    setHasChanges(JSON.stringify(localSettings) !== JSON.stringify(currentOriginal));
  }, [localSettings, originalWccSettingsBySymbol, symbolTab]);

  useEffect(() => {
    const currentOriginal = originalPvSettingsBySymbol[symbolTab];
    setPinningVolumeHasChanges(
      JSON.stringify(pinningVolumeSettings) !== JSON.stringify(currentOriginal)
    );
  }, [pinningVolumeSettings, originalPvSettingsBySymbol, symbolTab]);

  useEffect(() => {
    setIctHasChanges(JSON.stringify(ictSettings) !== JSON.stringify(originalIctSettings));
  }, [ictSettings, originalIctSettings]);

  // Track trailing candle settings changes
  useEffect(() => {
    setTrailingHasChanges(
      JSON.stringify(trailingSettings) !== JSON.stringify(originalTrailingSettings)
    );
  }, [trailingSettings, originalTrailingSettings]);

  // Track trailing pullback settings changes
  useEffect(() => {
    setTrailingPullbackHasChanges(
      JSON.stringify(trailingPullbackSettings) !== JSON.stringify(originalTrailingPullbackSettings)
    );
  }, [trailingPullbackSettings, originalTrailingPullbackSettings]);

  // Track magnet candle settings changes (per-symbol aware)
  useEffect(() => {
    if (strategyTab === 'magnet-candle') {
      const currentSettings = mcSettingsBySymbol[symbolTab] || magnetCandleSettings;
      const originalSettings = originalMcSettingsBySymbol[symbolTab] || originalMagnetCandleSettings;
      setMagnetCandleHasChanges(
        JSON.stringify(currentSettings) !== JSON.stringify(originalSettings)
      );
    }
  }, [magnetCandleSettings, originalMagnetCandleSettings, mcSettingsBySymbol, originalMcSettingsBySymbol, symbolTab, strategyTab]);

  // Track Donchian settings changes
  useEffect(() => {
    setDonchianHasChanges(
      JSON.stringify(donchianSettings) !== JSON.stringify(originalDonchianSettings)
    );
  }, [donchianSettings, originalDonchianSettings]);

  // Track Break of Structure (BOS) settings changes
  useEffect(() => {
    setBosHasChanges(
      JSON.stringify(bosSettings) !== JSON.stringify(originalBosSettings)
    );
  }, [bosSettings, originalBosSettings]);

  // Track volume profile settings changes
  useEffect(() => {
    setVpHasChanges(
      JSON.stringify(vpSettings) !== JSON.stringify(originalVpSettings)
    );
  }, [vpSettings, originalVpSettings]);

  // Track position alert settings changes
  useEffect(() => {
    setPosAlertHasChanges(
      JSON.stringify(posAlertSettings) !== JSON.stringify(originalPosAlertSettings)
    );
  }, [posAlertSettings, originalPosAlertSettings]);

  const handleApplyOptimizationResults = useCallback((mappings: ATRThresholdMapping[]) => {
    setPinningVolumeSettings((prev) => ({
      ...prev,
      dynamicThresholds: {
        ...prev.dynamicThresholds!,
        enabled: true,
        mappings,
      },
    }));
  }, []);

  if (!isOpen) return null;

  // Helper to save current WCC settings for a symbol
  const saveWccSettingsForSymbol = async (symbol: string, settings: typeof localSettings): Promise<boolean> => {
    const fullSettings = {
      ...DEFAULT_WHOLE_CANDLE_SETTINGS,
      ...settings,
      updatedAt: Date.now(),
    };
    try {
      console.log(`[GSM] Saving WCC settings for ${symbol} via REST`);
      const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/strategy/settings/per-symbol/${symbol}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullSettings),
      });
      if (!response.ok) {
        console.error(`[GSM] Failed to save WCC settings for ${symbol}: ${response.status}`);
        return false;
      }
      // Also update the in-memory Zustand store
      useSettingsStore.getState().setWholeCandleForSymbol(symbol, fullSettings);
      return true;
    } catch (error) {
      console.error(`[GSM] Failed to save WCC settings for ${symbol}:`, error);
      return false;
    }
  };

  // Recalculate handlers
  const handleRecalculateWCC = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);
    try {
      // IMPORTANT: Save current settings first if there are unsaved changes
      // This ensures recalculation uses the values shown in the UI
      if (hasChanges) {
        console.log(`[GSM] Saving unsaved WCC changes for ${symbolTab} before recalculating`);
        const saved = await saveWccSettingsForSymbol(symbolTab, localSettings);
        if (!saved) {
          setRecalculateResult({ type: 'error', message: 'Failed to save settings before recalculate' });
          return;
        }
        setOriginalWccSettingsBySymbol(prev => ({ ...prev, [symbolTab]: localSettings }));
        setHasChanges(false);
      }

      const result = await strategyService.recalculateMarkers(3);
      if (result.success) {
        setRecalculateResult({ type: 'success', message: `Recalculated ${result.count || 0} markers (last 3 days)` });
        // Fetch fresh markers to ensure UI is updated (WebSocket broadcast may be missed)
        try {
          const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/signal-markers?days=3`);
          if (response.ok) {
            const data = await response.json();
            if (data.markers) {
              const marketStore = useMarketStore.getState();
              // Preserve volume markers
              const existingVolumeMarkers = marketStore.signalMarkers.filter(
                m => m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish'
              );
              marketStore.setSignalMarkers([...data.markers, ...existingVolumeMarkers]);
              if (data.version !== undefined) {
                marketStore.setMarkersVersion(data.version);
              }
              console.log(`[GSM] Refreshed ${data.markers.length} markers after recalculation, version=${data.version}`);
            }
          }
        } catch (fetchErr) {
          console.warn('[GSM] Failed to fetch fresh markers:', fetchErr);
        }
      } else {
        setRecalculateResult({ type: 'error', message: result.error || 'Failed to recalculate' });
      }
    } catch (err) {
      setRecalculateResult({ type: 'error', message: 'Failed to recalculate markers' });
    } finally {
      setIsRecalculating(false);
      // Clear result after 3 seconds
      setTimeout(() => setRecalculateResult(null), 3000);
    }
  };

  // Helper to save current PV settings for a symbol
  const savePvSettingsForSymbol = async (symbol: string, settings: typeof pinningVolumeSettings): Promise<boolean> => {
    const fullSettings = {
      ...DEFAULT_PINNING_VOLUME_SETTINGS,
      ...settings,
      dynamicThresholds: {
        ...DEFAULT_DYNAMIC_THRESHOLDS,
        ...(settings.dynamicThresholds || {}),
      },
    };
    const ackPromise = waitForAcknowledgment('pinning-volume-settings-for-symbol-updated', symbol);
    console.log(`[GSM] Saving PV settings for symbol before recalculate:`, symbol);
    wsService.emit('update-pinning-volume-settings-for-symbol', { symbol, ...fullSettings });
    return await ackPromise;
  };

  const handleRecalculatePV = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);
    try {
      // IMPORTANT: Save current settings first if there are unsaved changes
      if (pinningVolumeHasChanges) {
        console.log(`[GSM] Saving unsaved PV changes for ${symbolTab} before recalculating`);
        const saved = await savePvSettingsForSymbol(symbolTab, pinningVolumeSettings);
        if (!saved) {
          setRecalculateResult({ type: 'error', message: 'Failed to save settings before recalculate' });
          return;
        }
        setOriginalPvSettingsBySymbol(prev => ({ ...prev, [symbolTab]: pinningVolumeSettings }));
        setPinningVolumeHasChanges(false);
      }

      const result = await strategyService.recalculateVolumeMarkers(3);
      if (result.success) {
        setRecalculateResult({ type: 'success', message: `Recalculated ${result.count || 0} markers (last 3 days)` });
        // Fetch fresh volume markers to ensure UI is updated
        try {
          const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/volume-markers?days=3`);
          if (response.ok) {
            const data = await response.json();
            if (data.markers) {
              const marketStore = useMarketStore.getState();
              // Preserve non-volume markers
              const nonVolumeMarkers = marketStore.signalMarkers.filter(
                m => m.type !== 'volume_spike_bullish' && m.type !== 'volume_spike_bearish'
              );
              marketStore.setSignalMarkers([...nonVolumeMarkers, ...data.markers]);
              console.log(`[GSM] Refreshed ${data.markers.length} volume markers after recalculation`);
            }
          }
        } catch (fetchErr) {
          console.warn('[GSM] Failed to fetch fresh volume markers:', fetchErr);
        }
      } else {
        setRecalculateResult({ type: 'error', message: result.error || 'Failed to recalculate' });
      }
    } catch (err) {
      setRecalculateResult({ type: 'error', message: 'Failed to recalculate markers' });
    } finally {
      setIsRecalculating(false);
      // Clear result after 3 seconds
      setTimeout(() => setRecalculateResult(null), 3000);
    }
  };

  const handleRecalculateTrailingPullback = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);
    try {
      // Save current settings first if there are unsaved changes
      if (trailingPullbackHasChanges) {
        console.log('[GSM] Saving unsaved Trailing Pullback changes before recalculating');
        try {
          await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              enabled: trailingPullbackSettings.enabled,
              confidenceThreshold: trailingPullbackSettings.confidenceThreshold,
              includeContinuation: trailingPullbackSettings.includeContinuation,
              requireWccAlignment: trailingPullbackSettings.requireWccAlignment,
              minTrailingBodyPct: trailingPullbackSettings.minTrailingBodyPct,
              updatedAt: Date.now(),
            }),
          });
          setOriginalTrailingPullbackSettings(trailingPullbackSettings);
          useSettingsStore.getState().setTrailingPullbackSettings(trailingPullbackSettings);
        } catch (saveErr) {
          setRecalculateResult({ type: 'error', message: 'Failed to save settings before recalculate' });
          return;
        }
      }

      const response = await fetch(
        `${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-markers/recalculate?days=3`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecalculateResult({ type: 'success', message: `Recalculated ${data.markersGenerated || 0} markers (last 3 days)` });
        } else {
          setRecalculateResult({ type: 'error', message: data.error || 'Failed to recalculate' });
        }
      } else {
        setRecalculateResult({ type: 'error', message: 'Failed to recalculate markers' });
      }
    } catch (err) {
      setRecalculateResult({ type: 'error', message: 'Failed to recalculate markers' });
    } finally {
      setIsRecalculating(false);
      setTimeout(() => setRecalculateResult(null), 3000);
    }
  };

  const handleRecalculateMagnetCandle = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);
    try {
      // Save current settings first if there are unsaved changes
      if (magnetCandleHasChanges) {
        try {
          await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              enabled: magnetCandleSettings.enabled,
              wickThreshold: magnetCandleSettings.wickThreshold,
              minBodyPct: magnetCandleSettings.minBodyPct,
              minRangePoints: magnetCandleSettings.minRangePoints,
              maxActiveMagnets: magnetCandleSettings.maxActiveMagnets,
              lookbackCandles: magnetCandleSettings.lookbackCandles,
              extensionLineCount: magnetCandleSettings.extensionLineCount,
              updatedAt: Date.now(),
            }),
          });
          setOriginalMagnetCandleSettings(magnetCandleSettings);
          useSettingsStore.getState().setMagnetCandleSettings(magnetCandleSettings);
        } catch (saveErr) {
          setRecalculateResult({ type: 'error', message: 'Failed to save settings before recalculate' });
          return;
        }
      }

      const response = await fetch(
        `${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-markers/recalculate`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecalculateResult({ type: 'success', message: `Recalculated ${data.markersGenerated || 0} magnet markers` });
        } else {
          setRecalculateResult({ type: 'error', message: data.error || 'Failed to recalculate' });
        }
      } else {
        setRecalculateResult({ type: 'error', message: 'Failed to recalculate magnet markers' });
      }
    } catch (err) {
      setRecalculateResult({ type: 'error', message: 'Failed to recalculate magnet markers' });
    } finally {
      setIsRecalculating(false);
      setTimeout(() => setRecalculateResult(null), 3000);
    }
  };

  // Handlers
  const handleSliderChange = (key: keyof typeof localSettings, value: number) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleChange = (key: keyof typeof localSettings) => {
    const wasEnabled = localSettings.enabled;
    setLocalSettings((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
    // Trigger recalculate when enabling (hide -> show)
    if (key === 'enabled' && !wasEnabled) {
      setTimeout(() => handleRecalculateWCC(), 100);
    }
  };

  const handlePVSliderChange = (key: keyof PinningVolumeSettings, value: number) => {
    setPinningVolumeSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handlePVToggleChange = (key: keyof PinningVolumeSettings) => {
    const wasEnabled = pinningVolumeSettings.enabled;
    setPinningVolumeSettings((prev) => {
      const newValue = !prev[key as keyof typeof prev];

      // Make requireAlignedWick and useWickDirection mutually exclusive
      if (key === 'requireAlignedWick' && newValue) {
        return { ...prev, requireAlignedWick: true, useWickDirection: false };
      }
      if (key === 'useWickDirection' && newValue) {
        return { ...prev, useWickDirection: true, requireAlignedWick: false };
      }

      return { ...prev, [key]: newValue };
    });
    // Trigger recalculate when enabling (hide -> show)
    if (key === 'enabled' && !wasEnabled) {
      setTimeout(() => handleRecalculatePV(), 100);
    }
  };

  const handleDynamicThresholdToggle = () => {
    setPinningVolumeSettings((prev) => ({
      ...prev,
      dynamicThresholds: {
        ...prev.dynamicThresholds!,
        enabled: !prev.dynamicThresholds?.enabled,
      },
    }));
  };

  // ICT handlers
  const handleICTSliderChange = (key: keyof ICTSettings, value: number) => {
    setIctSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleICTToggleChange = (key: keyof ICTSettings) => {
    const wasEnabled = ictSettings.enabled;
    setIctSettings((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
    // Trigger recalculate when enabling (hide -> show)
    if (key === 'enabled' && !wasEnabled) {
      setTimeout(() => handleRecalculateICT(), 100);
    }
  };

  const handleICTTimeframeToggle = (settingsKey: 'fvgTimeframes' | 'ifvgTimeframes', timeframe: ICTTimeframe) => {
    setIctSettings((prev) => {
      const currentTimeframes = prev[settingsKey] as ICTTimeframe[];
      const isEnabled = currentTimeframes.includes(timeframe);
      if (isEnabled) {
        // Remove timeframe (but ensure at least one remains)
        if (currentTimeframes.length > 1) {
          return { ...prev, [settingsKey]: currentTimeframes.filter(tf => tf !== timeframe) };
        }
        return prev; // Don't remove if it's the last one
      } else {
        // Add timeframe
        return { ...prev, [settingsKey]: [...currentTimeframes, timeframe] };
      }
    });
  };

  const handleICTColorChange = (key: keyof ICTSettings, value: string) => {
    setIctSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleLookbackChange = (settingsKey: 'fvgLookbackByTimeframe' | 'ifvgLookbackByTimeframe', timeframe: string, value: number) => {
    setIctSettings(prev => ({
      ...prev,
      [settingsKey]: { ...prev[settingsKey], [timeframe]: value }
    }));
  };

  const handleRecalculateICT = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);

    try {
      const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/fvg-zones/recalculate?days=3`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        setRecalculateResult({ type: 'success', message: `${data.zoneCount || 0} zones recalculated` });
      } else {
        setRecalculateResult({ type: 'error', message: 'Failed to recalculate' });
      }
    } catch (err) {
      setRecalculateResult({ type: 'error', message: 'Error recalculating' });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleMappingChange = (
    index: number,
    field: keyof ATRThresholdMapping,
    value: number | string
  ) => {
    setPinningVolumeSettings((prev) => {
      const newMappings = [...prev.dynamicThresholds!.mappings];
      newMappings[index] = { ...newMappings[index], [field]: value };
      return {
        ...prev,
        dynamicThresholds: {
          ...prev.dynamicThresholds!,
          mappings: newMappings,
        },
      };
    });
  };

  // Helper to wait for server acknowledgment with timeout
  const waitForAcknowledgment = (eventName: string, symbol: string, timeout = 5000): Promise<boolean> => {
    return new Promise((resolve) => {
      const handler = (data: { success?: boolean; symbol?: string }) => {
        if (data.symbol === symbol) {
          wsService.off(eventName, handler);
          resolve(data.success === true);
        }
      };
      wsService.on(eventName, handler);
      // Timeout fallback
      setTimeout(() => {
        wsService.off(eventName, handler);
        resolve(false);
      }, timeout);
    });
  };

  const handleSave = async () => {
    setSyncing(true);
    try {
      if (activeSection === 'strategy') {
        if (strategyTab === 'whole-candle') {
          // Save per-symbol WCC settings via REST
          const saved = await saveWccSettingsForSymbol(symbolTab, localSettings);
          if (!saved) {
            setSyncError('Failed to save WCC settings');
            alert('Failed to save settings. Please try again.');
            return;
          }

          console.log('[GSM] WCC settings saved successfully for:', symbolTab);
          setOriginalWccSettingsBySymbol(prev => ({ ...prev, [symbolTab]: localSettings }));
          setHasChanges(false);
          setSyncSuccess();

          // Also update global store if NQ (for backward compat)
          if (symbolTab === 'NQ') {
            updateSettings(localSettings);
          }

          // Recalculate markers with new settings (last 3 days)
          await strategyService.recalculateMarkers(3);

        } else if (strategyTab === 'pinning-volume') {
          // Save per-symbol PV settings via REST
          const fullSettings = {
            ...DEFAULT_PINNING_VOLUME_SETTINGS,
            ...pinningVolumeSettings,
            dynamicThresholds: {
              ...DEFAULT_DYNAMIC_THRESHOLDS,
              ...(pinningVolumeSettings.dynamicThresholds || {}),
            },
            updatedAt: Date.now(),
          };
          try {
            console.log('[GSM] Saving PV settings for symbol via REST:', symbolTab);
            const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/strategy/settings/per-symbol-pv/${symbolTab}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(fullSettings),
            });
            if (!response.ok) throw new Error('Server returned ' + response.status);

            console.log('[GSM] PV settings saved successfully for:', symbolTab);
            setOriginalPvSettingsBySymbol(prev => ({ ...prev, [symbolTab]: pinningVolumeSettings }));
            setPinningVolumeHasChanges(false);
            setSyncSuccess();

            // Update Zustand store
            useSettingsStore.getState().setPinningVolumeForSymbol(symbolTab, fullSettings);

            // Also update global store if NQ (for backward compat)
            if (symbolTab === 'NQ') {
              updatePVStoreSettings({ enabled: pinningVolumeSettings.enabled });
            }

            // Recalculate volume markers with new settings
            await strategyService.recalculateVolumeMarkers();
          } catch (error) {
            console.error('[GSM] Failed to save PV settings:', error);
            setSyncError('Failed to save PV settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'ict') {
          // Save ICT settings via REST
          const fullSettings = {
            ...DEFAULT_ICT_SETTINGS,
            ...ictSettings,
          };
          try {
            console.log('[GSM] Saving ICT settings via REST');
            const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/ict-settings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(fullSettings),
            });
            if (!response.ok) throw new Error('Server returned ' + response.status);

            console.log('[GSM] ICT settings saved successfully');
            setOriginalIctSettings(ictSettings);
            useSettingsStore.getState().setICTSettings(fullSettings);
            setIctHasChanges(false);
            setSyncSuccess();

            // Recalculate FVG zones with new settings
            try {
              const recalcResp = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/fvg-zones/recalculate?days=3`, {
                method: 'POST',
              });
              if (recalcResp.ok) {
                const data = await recalcResp.json();
                console.log(`[GSM] Recalculated ${data.count || 0} FVG zones`);
              }
            } catch (err) {
              console.warn('[GSM] Failed to recalculate FVG zones:', err);
            }
          } catch (error) {
            console.error('[GSM] Failed to save ICT settings:', error);
            setSyncError('Failed to save ICT settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'volume-profile') {
          // Save volume profile settings via store (which uses REST API)
          try {
            useVolumeProfileStore.getState().setSettings(vpSettings);
            await useVolumeProfileStore.getState().saveSettings();

            console.log('[GSM] Volume profile settings saved successfully');
            setOriginalVpSettings(vpSettings);
            setVpHasChanges(false);
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save volume profile settings:', error);
            setSyncError('Failed to save volume profile settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'magnet-candle') {
          // Save per-symbol magnet candle settings via REST API
          const settingsToSave = mcSettingsBySymbol[symbolTab] || magnetCandleSettings;
          const saveUrl = `${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings/${symbolTab}`;
          try {
            const saveResponse = await fetch(saveUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                enabled: settingsToSave.enabled,
                wickThreshold: settingsToSave.wickThreshold,
                minBodyPct: settingsToSave.minBodyPct,
                minRangePoints: settingsToSave.minRangePoints,
                maxActiveMagnets: settingsToSave.maxActiveMagnets,
                lookbackCandles: settingsToSave.lookbackCandles,
                extensionLineCount: settingsToSave.extensionLineCount,
                updatedAt: Date.now(),
              }),
            });
            if (!saveResponse.ok) throw new Error('Server returned ' + saveResponse.status);

            console.log('[GSM] Magnet candle settings saved for:', symbolTab);
            setOriginalMcSettingsBySymbol(prev => ({ ...prev, [symbolTab]: settingsToSave }));
            useSettingsStore.getState().setMagnetCandleForSymbol(symbolTab, settingsToSave);
            setMagnetCandleHasChanges(false);
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save magnet candle settings:', error);
            setSyncError('Failed to save magnet candle settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'sr-alerts') {
          const saveUrl = `${CONFIG.getBackendUrl(window.location.hostname)}/api/sr-proximity-settings`;
          try {
            const saveResponse = await fetch(saveUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(srAlertSettings),
            });
            if (!saveResponse.ok) throw new Error('Server returned ' + saveResponse.status);
            useSettingsStore.getState().setSRProximityAlertSettings(srAlertSettings);
            setSrAlertHasChanges(false);
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save SR alert settings:', error);
            setSyncError('Failed to save SR alert settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'donchian') {
          // Save donchian settings via REST API
          try {
            const saveResponse = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/donchian-settings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(donchianSettings),
            });
            if (!saveResponse.ok) throw new Error('Server returned ' + saveResponse.status);

            console.log('[GSM] Donchian settings saved');
            setOriginalDonchianSettings(donchianSettings);
            useDonchianSettingsStore.getState().setSettings(donchianSettings);
            setDonchianHasChanges(false);
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save donchian settings:', error);
            setSyncError('Failed to save donchian settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'break-of-structure') {
          // Save Break of Structure (BOS) settings via REST API
          try {
            const payload = { ...bosSettings, updatedAt: Date.now() };
            const saveResponse = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-settings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!saveResponse.ok) throw new Error('Server returned ' + saveResponse.status);

            // Recalculate markers from historical candles after saving
            await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-markers/recalculate?days=3`, { method: 'POST' });

            console.log('[GSM] BOS settings saved');
            setBosSettings(payload);
            setOriginalBosSettings(payload);
            useSettingsStore.getState().setBosSettings(payload);
            setBosHasChanges(false);
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save BOS settings:', error);
            setSyncError('Failed to save BOS settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        } else if (strategyTab === 'trailing-pullback') {
          // Save both trailing candle and trailing pullback settings
          try {
            if (trailingHasChanges) {
              const resp = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-candle-settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  enabled: trailingSettings.enabled,
                  duration_seconds: trailingSettings.durationSeconds,
                }),
              });
              if (!resp.ok) throw new Error('Server returned ' + resp.status);
              setOriginalTrailingSettings(trailingSettings);
              useSettingsStore.getState().setTrailingCandleSettings(trailingSettings);
            }
            if (trailingPullbackHasChanges) {
              const resp = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  enabled: trailingPullbackSettings.enabled,
                  confidenceThreshold: trailingPullbackSettings.confidenceThreshold,
                  includeContinuation: trailingPullbackSettings.includeContinuation,
                  requireWccAlignment: trailingPullbackSettings.requireWccAlignment,
                  minTrailingBodyPct: trailingPullbackSettings.minTrailingBodyPct,
                  updatedAt: Date.now(),
                }),
              });
              if (!resp.ok) throw new Error('Server returned ' + resp.status);
              setOriginalTrailingPullbackSettings(trailingPullbackSettings);
              useSettingsStore.getState().setTrailingPullbackSettings(trailingPullbackSettings);
            }
            console.log('[GSM] Trailing settings saved');
            setSyncSuccess();
          } catch (error) {
            console.error('[GSM] Failed to save trailing settings:', error);
            setSyncError('Failed to save trailing settings');
            alert('Failed to save settings. Please try again.');
            return;
          }
        }
      }
      onClose();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleReset = () => {
    if (activeSection === 'strategy') {
      if (strategyTab === 'whole-candle') {
        setLocalSettings(DEFAULT_WHOLE_CANDLE_SETTINGS);
      } else if (strategyTab === 'pinning-volume') {
        setPinningVolumeSettings(DEFAULT_PINNING_VOLUME_SETTINGS);
      } else if (strategyTab === 'ict') {
        setIctSettings(DEFAULT_ICT_SETTINGS);
      } else if (strategyTab === 'volume-profile') {
        setVpSettings(DEFAULT_VOLUME_PROFILE_SETTINGS);
      } else if (strategyTab === 'donchian') {
        setDonchianSettings(DEFAULT_DONCHIAN_SETTINGS);
      } else if (strategyTab === 'break-of-structure') {
        setBosSettings(DEFAULT_BOS_SETTINGS);
      } else if (strategyTab === 'magnet-candle') {
        setMcSettingsBySymbol(prev => ({ ...prev, [symbolTab]: DEFAULT_MAGNET_CANDLE_SETTINGS }));
      } else if (strategyTab === 'trailing-pullback') {
        setTrailingSettings(DEFAULT_TRAILING_CANDLE_SETTINGS);
        setTrailingPullbackSettings(DEFAULT_TRAILING_PULLBACK_SETTINGS);
      } else if (strategyTab === 'volume-markers') {
        useVestSpikeSettingsStore.getState().reset();
        useVolumePaneSettingsStore.getState().setCme({ ...DEFAULT_CME_SPIKE_SETTINGS });
        useVolumePaneSettingsStore.getState().setShowSpikeMultiples(true);
        const levels = useLevelsStore.getState();
        for (const k of ['vestSpikes', 'vestVolSpikes', 'cmeSpikes', 'cmeVolSpikes', 'bothSpikes', 'vestCat'] as const) levels.setLevelVisible(k, true);
      } else if (strategyTab === 'wick-rejection') {
        useWickRejectionSettingsStore.getState().reset();
        useLevelsStore.getState().setLevelVisible('wickRejection', true);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSectionChange = (section: SidebarSection) => {
    setActiveSection(section);
    setIsMobileSidebarOpen(false);
  };

  const currentHasChanges =
    activeSection === 'strategy'
      ? strategyTab === 'whole-candle'
        ? hasChanges
        : strategyTab === 'pinning-volume'
        ? pinningVolumeHasChanges
        : strategyTab === 'ict'
        ? ictHasChanges
        : strategyTab === 'donchian'
        ? donchianHasChanges
        : strategyTab === 'break-of-structure'
        ? bosHasChanges
        : strategyTab === 'trailing-pullback'
        ? (trailingHasChanges || trailingPullbackHasChanges)
        : strategyTab === 'magnet-candle'
        ? magnetCandleHasChanges
        : strategyTab === 'sr-alerts'
        ? srAlertHasChanges
        : strategyTab === 'volume-markers' || strategyTab === 'wick-rejection'
        ? false
        : vpHasChanges
      : false;

  // Render General section content
  const renderGeneralContent = () => {
    switch (generalTab) {
      case 'appearance':
        return (
          <div className="gs-tab-content">
            <div className="gs-section">
              <div className="gs-section-header">
                <span className="gs-section-icon">🎨</span>
                <span className="gs-section-title">Theme</span>
              </div>
              <div className="gs-theme-grid">
                {Object.values(themes).map((theme) => (
                  <button
                    key={theme.id}
                    className={`gs-theme-card ${currentTheme.id === theme.id ? 'active' : ''}`}
                    onClick={() => setTheme(theme.id)}
                  >
                    <div className="gs-theme-preview">
                      <div
                        className="gs-theme-color"
                        style={{ backgroundColor: theme.colors.bgPrimary }}
                      />
                      <div
                        className="gs-theme-color"
                        style={{ backgroundColor: theme.colors.accent }}
                      />
                      <div
                        className="gs-theme-color"
                        style={{ backgroundColor: theme.colors.success }}
                      />
                    </div>
                    <div className="gs-theme-info">
                      <span className="gs-theme-name">{theme.name}</span>
                      {currentTheme.id === theme.id && <span className="gs-theme-check">✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="gs-tab-content">
            <div className="gs-section">
              <div className="gs-section-header">
                <span className="gs-section-icon">📍</span>
                <span className="gs-section-title">Position Alerts</span>
              </div>
              <p className="gs-section-description">
                Receive alerts on your Android widget when positions open, close, hit drawdown limits, or approach take-profit targets.
              </p>

              {/* Position Opened Toggle */}
              <div className="gs-row gs-toggle-row">
                <div className="gs-label">
                  <span className="gs-label-main">Position Opened</span>
                  <span className="gs-label-hint">Alert when a new position is filled</span>
                </div>
                <label className="gs-toggle-switch">
                  <input
                    type="checkbox"
                    checked={posAlertSettings.positionOpened}
                    onChange={(e) => setPosAlertSettings(prev => ({ ...prev, positionOpened: e.target.checked }))}
                  />
                  <span className="gs-toggle-slider"></span>
                </label>
              </div>

              {/* Position Closed Toggle */}
              <div className="gs-row gs-toggle-row">
                <div className="gs-label">
                  <span className="gs-label-main">Position Closed</span>
                  <span className="gs-label-hint">Alert when a position is flattened</span>
                </div>
                <label className="gs-toggle-switch">
                  <input
                    type="checkbox"
                    checked={posAlertSettings.positionClosed}
                    onChange={(e) => setPosAlertSettings(prev => ({ ...prev, positionClosed: e.target.checked }))}
                  />
                  <span className="gs-toggle-slider"></span>
                </label>
              </div>

              {/* Drawdown Alert Toggle + Threshold */}
              <div className="gs-row gs-toggle-row">
                <div className="gs-label">
                  <span className="gs-label-main">Drawdown Alert</span>
                  <span className="gs-label-hint">Alert when unrealized loss exceeds threshold</span>
                </div>
                <label className="gs-toggle-switch">
                  <input
                    type="checkbox"
                    checked={posAlertSettings.drawdownEnabled}
                    onChange={(e) => setPosAlertSettings(prev => ({ ...prev, drawdownEnabled: e.target.checked }))}
                  />
                  <span className="gs-toggle-slider"></span>
                </label>
              </div>
              {posAlertSettings.drawdownEnabled && (
                <div className="gs-row">
                  <div className="gs-label">
                    <span className="gs-label-main">Drawdown Threshold (pts)</span>
                    <span className="gs-label-hint">Fire alert when drawdown exceeds this many points</span>
                  </div>
                  <input
                    type="number"
                    className="gs-input gs-input-sm"
                    value={posAlertSettings.drawdownThresholdPts}
                    min={1}
                    max={500}
                    step={1}
                    onChange={(e) => setPosAlertSettings(prev => ({
                      ...prev,
                      drawdownThresholdPts: Math.max(1, Math.min(500, Number(e.target.value) || 30))
                    }))}
                  />
                </div>
              )}

              {/* TP Approaching Toggle + Percentage */}
              <div className="gs-row gs-toggle-row">
                <div className="gs-label">
                  <span className="gs-label-main">TP Approaching</span>
                  <span className="gs-label-hint">Alert when price nears take-profit target</span>
                </div>
                <label className="gs-toggle-switch">
                  <input
                    type="checkbox"
                    checked={posAlertSettings.tpApproachingEnabled}
                    onChange={(e) => setPosAlertSettings(prev => ({ ...prev, tpApproachingEnabled: e.target.checked }))}
                  />
                  <span className="gs-toggle-slider"></span>
                </label>
              </div>
              {posAlertSettings.tpApproachingEnabled && (
                <div className="gs-row">
                  <div className="gs-label">
                    <span className="gs-label-main">TP Approach %</span>
                    <span className="gs-label-hint">Fire alert when price reaches this % of distance to TP</span>
                  </div>
                  <input
                    type="number"
                    className="gs-input gs-input-sm"
                    value={posAlertSettings.tpApproachPct}
                    min={50}
                    max={100}
                    step={5}
                    onChange={(e) => setPosAlertSettings(prev => ({
                      ...prev,
                      tpApproachPct: Math.max(50, Math.min(100, Number(e.target.value) || 90))
                    }))}
                  />
                </div>
              )}

              {/* Save Button */}
              {posAlertHasChanges && (
                <div className="gs-row gs-save-row">
                  <button
                    className="gs-btn gs-btn-primary"
                    onClick={async () => {
                      try {
                        await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/position-alert-settings`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(posAlertSettings),
                        });
                        setOriginalPosAlertSettings(posAlertSettings);
                      } catch (err) {
                        console.error('Failed to save position alert settings:', err);
                        alert('Failed to save settings. Please try again.');
                      }
                    }}
                    disabled={isSyncing}
                  >
                    {isSyncing ? 'Saving...' : 'Save Position Alert Settings'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );


      case 'trading':
        return (
          <div className="gs-tab-content">
            <div className="gs-section">
              <div className="gs-section-header">
                <span className="gs-section-icon">🧾</span>
                <span className="gs-section-title">Order Entry</span>
              </div>
              <p className="gs-section-description">
                Applies to both the desktop order panel and the mobile trading bar.
              </p>

              <div className="gs-row">
                <div className="gs-label">
                  <span className="gs-label-main">Default Order Quantity</span>
                  <span className="gs-label-hint">
                    Where a fresh ticket starts, and what the size returns to when a position closes
                  </span>
                </div>
                <input
                  type="number"
                  className="gs-input gs-input-sm"
                  aria-label="Default Order Quantity"
                  value={defaultQtyDraft}
                  min={MIN_ORDER_QUANTITY}
                  max={MAX_ORDER_QUANTITY}
                  step={1}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDefaultQtyDraft(next);
                    // Rejected outright while the field holds junk — the stored
                    // default keeps its previous value rather than going NaN.
                    setDefaultQuantity(next);
                  }}
                  onBlur={() => setDefaultQtyDraft(String(defaultQuantity))}
                />
              </div>

              <div className="gs-row">
                <div className="gs-label">
                  <span className="gs-label-main">Quick Pick</span>
                  <span className="gs-label-hint">Common sizes</span>
                </div>
                <div className="gs-qty-presets">
                  {QUANTITY_PRESETS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className={`gs-btn gs-qty-preset ${defaultQuantity === q ? 'active' : ''}`}
                      onClick={() => {
                        if (setDefaultQuantity(q)) setDefaultQtyDraft(String(q));
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'account':
        return (
          <div className="gs-tab-content">
            <div className="gs-section">
              <div className="gs-section-header">
                <span className="gs-section-icon">🔐</span>
                <span className="gs-section-title">Session</span>
              </div>
              <div className="gs-account-section">
                <p className="gs-account-info">
                  You are currently logged in with a PIN-protected session.
                  Sessions expire after 6 hours of inactivity.
                </p>
                <button
                  className="gs-btn gs-btn-logout"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  // Render Strategy section content
  const renderStrategyContent = () => {
    // 🐋 / 🦈 / 🐋🦈 / 🐱 volume markers: client-side, applies immediately (no Save).
    if (strategyTab === 'volume-markers') return <VolumeMarkersSettingsPanel />;
    // 🪝 wick rejection: client-side, applies immediately (no Save).
    if (strategyTab === 'wick-rejection') return <WickRejectionSettingsPanel />;
    if (strategyTab === 'whole-candle') {
      return (
        <div className="gs-tab-content">
          {/* Strategy Enable Toggle + Recalculate */}
          <div className="gs-section gs-strategy-enable">
            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span className="gs-label-main">Show Markers</span>
                <span className="gs-label-hint">Display whole candle signal markers on chart</span>
              </div>
              <label className="gs-toggle-switch">
                <input
                  type="checkbox"
                  checked={localSettings.enabled}
                  onChange={() => handleToggleChange('enabled')}
                />
                <span className="gs-toggle-slider"></span>
              </label>
            </div>
            <div className="gs-row gs-recalculate-row">
              <button
                className="gs-btn gs-btn-recalculate"
                onClick={handleRecalculateWCC}
                disabled={isRecalculating}
              >
                {isRecalculating ? 'Recalculating...' : 'Recalculate Markers'}
              </button>
              {recalculateResult && (
                <span className={`gs-recalculate-result ${recalculateResult.type}`}>
                  {recalculateResult.message}
                </span>
              )}
            </div>
          </div>

          {/* Wick Threshold Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon">W</span>
              <span className="gs-section-title">Wick Threshold</span>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Minimum</span>
                <span className="gs-value">{localSettings.wickThresholdMin}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                value={localSettings.wickThresholdMin}
                onChange={(e) => handleSliderChange('wickThresholdMin', Number(e.target.value))}
                className="gs-slider"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Maximum</span>
                <span className="gs-value">{localSettings.wickThresholdMax}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={localSettings.wickThresholdMax}
                onChange={(e) => handleSliderChange('wickThresholdMax', Number(e.target.value))}
                className="gs-slider"
              />
            </div>
          </div>

          {/* Signal Timing Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon">⏱</span>
              <span className="gs-section-title">Signal Timing</span>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Preemptive</span>
                <span className="gs-value">{localSettings.preemptiveSeconds}s before close</span>
              </div>
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={localSettings.preemptiveSeconds}
                onChange={(e) => handleSliderChange('preemptiveSeconds', Number(e.target.value))}
                className="gs-slider"
              />
            </div>
          </div>

          {/* Trade Settings Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon">T</span>
              <span className="gs-section-title">Trade Settings</span>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Take Profit</span>
                <span className="gs-value green">{localSettings.tpPoints} pts</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={localSettings.tpPoints}
                onChange={(e) => handleSliderChange('tpPoints', Number(e.target.value))}
                className="gs-slider green"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Stop Loss</span>
                <span className="gs-value red">{localSettings.slPoints} pts</span>
              </div>
              <input
                type="range"
                min="1"
                max="200"
                value={localSettings.slPoints}
                onChange={(e) => handleSliderChange('slPoints', Number(e.target.value))}
                className="gs-slider red"
              />
            </div>
          </div>

          {/* Skip Conditions Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon">S</span>
              <span className="gs-section-title">Skip Conditions</span>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Skip at HOD</span>
                <span className="gs-label-hint">High of Day</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.skipIfHOD ? 'active' : ''}`}
                onClick={() => handleToggleChange('skipIfHOD')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Skip at LOD</span>
                <span className="gs-label-hint">Low of Day</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.skipIfLOD ? 'active' : ''}`}
                onClick={() => handleToggleChange('skipIfLOD')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>HOD/LOD Range</span>
                <span className="gs-value yellow">{localSettings.hodLodRangePoints} pts</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={localSettings.hodLodRangePoints}
                onChange={(e) => handleSliderChange('hodLodRangePoints', Number(e.target.value))}
                className="gs-slider yellow"
              />
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Skip Small Candles</span>
                <span className="gs-label-hint">Min candle length</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.skipIfSmallCandle ? 'active' : ''}`}
                onClick={() => handleToggleChange('skipIfSmallCandle')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Min Candle Length</span>
                <span className="gs-value cyan">{localSettings.minCandleLengthPoints} pts</span>
              </div>
              <input
                type="range"
                min="1"
                max="30"
                value={localSettings.minCandleLengthPoints}
                onChange={(e) =>
                  handleSliderChange('minCandleLengthPoints', Number(e.target.value))
                }
                className="gs-slider cyan"
              />
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Skip Low ATR</span>
                <span className="gs-label-hint">Low volatility</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.skipIfLowATR ? 'active' : ''}`}
                onClick={() => handleToggleChange('skipIfLowATR')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>ATR Min Threshold</span>
                <span className="gs-value cyan">{localSettings.atrMinThreshold} pts</span>
              </div>
              <input
                type="range"
                min="1"
                max="20"
                value={localSettings.atrMinThreshold}
                onChange={(e) =>
                  handleSliderChange('atrMinThreshold', Number(e.target.value))
                }
                className="gs-slider cyan"
              />
            </div>
          </div>

          {/* Confidence Scoring Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon purple">C</span>
              <span className="gs-section-title">Confidence Scoring</span>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Enable Confidence</span>
                <span className="gs-label-hint">Score signals by technical levels</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.confidenceEnabled ? 'active' : ''}`}
                onClick={() => handleToggleChange('confidenceEnabled')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Min Threshold</span>
                <span className="gs-value purple">{localSettings.confidenceMinThreshold}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={localSettings.confidenceMinThreshold}
                onChange={(e) => handleSliderChange('confidenceMinThreshold', Number(e.target.value))}
                className="gs-slider purple"
              />
            </div>

            <div className="gs-subsection-header">Level Groups</div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Use EMAs</span>
                <span className="gs-label-hint">EMA 9, 21, 200</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.useEmas ? 'active' : ''}`}
                onClick={() => handleToggleChange('useEmas')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Use Fib Levels</span>
                <span className="gs-label-hint">38.2%, 50%, 61.8%, 70.5%, 78.6%</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.useFibs ? 'active' : ''}`}
                onClick={() => handleToggleChange('useFibs')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Use VWAP</span>
                <span className="gs-label-hint">Volume weighted average price</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.useVwap ? 'active' : ''}`}
                onClick={() => handleToggleChange('useVwap')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Use HOD/LOD</span>
                <span className="gs-label-hint">High/Low of Day</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.useHodLod ? 'active' : ''}`}
                onClick={() => handleToggleChange('useHodLod')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-subsection-header">EMA Weights</div>

            <div className="gs-row">
              <div className="gs-label">
                <span>EMA 9</span>
                <span className="gs-value">{localSettings.weightEma9.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightEma9}
                onChange={(e) => handleSliderChange('weightEma9', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useEmas}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>EMA 21</span>
                <span className="gs-value">{localSettings.weightEma21.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightEma21}
                onChange={(e) => handleSliderChange('weightEma21', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useEmas}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>EMA 200</span>
                <span className="gs-value">{localSettings.weightEma200.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightEma200}
                onChange={(e) => handleSliderChange('weightEma200', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useEmas}
              />
            </div>

            <div className="gs-subsection-header">Fib Weights</div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Fib 38.2%</span>
                <span className="gs-value">{localSettings.weightFib382.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightFib382}
                onChange={(e) => handleSliderChange('weightFib382', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useFibs}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Fib 50%</span>
                <span className="gs-value">{localSettings.weightFib50.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightFib50}
                onChange={(e) => handleSliderChange('weightFib50', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useFibs}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Fib 61.8%</span>
                <span className="gs-value">{localSettings.weightFib618.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightFib618}
                onChange={(e) => handleSliderChange('weightFib618', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useFibs}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Fib 70.5%</span>
                <span className="gs-value">{(localSettings.weightFib705 ?? 1.0).toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightFib705 ?? 1.0}
                onChange={(e) => handleSliderChange('weightFib705', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useFibs}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Fib 78.6%</span>
                <span className="gs-value">{localSettings.weightFib786.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightFib786}
                onChange={(e) => handleSliderChange('weightFib786', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useFibs}
              />
            </div>

            <div className="gs-subsection-header">VWAP & HOD/LOD</div>

            <div className="gs-row">
              <div className="gs-label">
                <span>VWAP</span>
                <span className="gs-value">{localSettings.weightVwap.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightVwap}
                onChange={(e) => handleSliderChange('weightVwap', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useVwap}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>HOD</span>
                <span className="gs-value">{localSettings.weightHod.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightHod}
                onChange={(e) => handleSliderChange('weightHod', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useHodLod}
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>LOD</span>
                <span className="gs-value">{localSettings.weightLod.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="3"
                step="0.1"
                value={localSettings.weightLod}
                onChange={(e) => handleSliderChange('weightLod', Number(e.target.value))}
                className="gs-slider"
                disabled={!localSettings.useHodLod}
              />
            </div>

            <div className="gs-subsection-header">Bonuses & Penalties</div>

            <div className="gs-row">
              <div className="gs-label">
                <span>EMA Stack Bonus</span>
                <span className="gs-value green">+{localSettings.emaStackBonus.toFixed(0)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                value={localSettings.emaStackBonus}
                onChange={(e) => handleSliderChange('emaStackBonus', Number(e.target.value))}
                className="gs-slider green"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>VWAP Alignment</span>
                <span className="gs-value green">+{localSettings.vwapAlignmentBonus.toFixed(0)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                value={localSettings.vwapAlignmentBonus}
                onChange={(e) => handleSliderChange('vwapAlignmentBonus', Number(e.target.value))}
                className="gs-slider green"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Cluster Penalty</span>
                <span className="gs-value red">-{localSettings.levelDensityPenalty.toFixed(0)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                value={localSettings.levelDensityPenalty}
                onChange={(e) => handleSliderChange('levelDensityPenalty', Number(e.target.value))}
                className="gs-slider red"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Cluster Threshold</span>
                <span className="gs-value">{localSettings.levelDensityThreshold} levels</span>
              </div>
              <input
                type="range"
                min="1"
                max="6"
                value={localSettings.levelDensityThreshold}
                onChange={(e) => handleSliderChange('levelDensityThreshold', Number(e.target.value))}
                className="gs-slider"
              />
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>Next Level Penalty</span>
                <span className="gs-value red">-{localSettings.nextLevelClosePenalty.toFixed(0)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                value={localSettings.nextLevelClosePenalty}
                onChange={(e) => handleSliderChange('nextLevelClosePenalty', Number(e.target.value))}
                className="gs-slider red"
              />
            </div>
          </div>

          {/* Confluence Scoring Section */}
          <div className="gs-section">
            <div className="gs-section-header">
              <span className="gs-section-icon orange">CF</span>
              <span className="gs-section-title">Confluence Scoring</span>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Enable Confluence</span>
                <span className="gs-label-hint">Check correlated symbols (NQ, MNQ, ES, MES) for signals on same bar</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.confluenceEnabled ? 'active' : ''}`}
                onClick={() => handleToggleChange('confluenceEnabled')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Include Skipped</span>
                <span className="gs-label-hint">Count skipped signals in confluence calculation</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.confluenceIncludeSkipped ? 'active' : ''}`}
                onClick={() => handleToggleChange('confluenceIncludeSkipped')}
                disabled={!localSettings.confluenceEnabled}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>Show Other Symbols</span>
                <span className="gs-label-hint">Display markers when correlated symbols have signals</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.showOtherSymbolSignals ? 'active' : ''}`}
                onClick={() => handleToggleChange('showOtherSymbolSignals')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            {/* TAKE IT Indicator Settings */}
            <div className="gs-row gs-toggle-row">
              <div className="gs-label">
                <span>🔥 TAKE IT Indicator</span>
                <span className="gs-label-hint">Show "TAKE IT" when confidence + confluence exceeds threshold</span>
              </div>
              <button
                className={`gs-toggle ${localSettings.takeItEnabled !== false ? 'active' : ''}`}
                onClick={() => handleToggleChange('takeItEnabled')}
              >
                <span className="gs-toggle-knob" />
              </button>
            </div>

            <div className="gs-row">
              <div className="gs-label">
                <span>TAKE IT Threshold</span>
                <span className="gs-value magenta">{localSettings.takeItThreshold ?? 80}</span>
              </div>
              <input
                type="range"
                min={50}
                max={150}
                value={localSettings.takeItThreshold ?? 80}
                onChange={(e) => handleSliderChange('takeItThreshold', Number(e.target.value))}
                className="gs-slider magenta"
              />
            </div>

            <div className="gs-info-box orange">
              <div className="gs-info-title">Scoring</div>
              <div className="gs-info-content">
                <div>Same direction: <span className="green">+30</span></div>
                <div>Opposite direction: <span className="red">-20</span></div>
                <div>Skipped (same dir): <span className="green">+15</span></div>
                <div>Full confirms micro: <span className="green">+10</span></div>
                <div>Cross-index (NQ↔ES): <span className="green">+15</span></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Pinning Volume tab
    if (strategyTab === 'pinning-volume') {
    return (
      <div className="gs-tab-content">
        {/* Strategy Enable Toggle + Recalculate */}
        <div className="gs-section gs-strategy-enable">
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Show Markers</span>
              <span className="gs-label-hint">Display pinning volume signal markers on chart</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={pinningVolumeSettings.enabled}
                onChange={() => handlePVToggleChange('enabled')}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={handleRecalculatePV}
              disabled={isRecalculating}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate Markers'}
            </button>
            {recalculateResult && (
              <span className={`gs-recalculate-result ${recalculateResult.type}`}>
                {recalculateResult.message}
              </span>
            )}
          </div>
        </div>

        {/* Dynamic Thresholds Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon blue">D</span>
            <span className="gs-section-title">Dynamic Thresholds</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Enable Dynamic</span>
              <span className="gs-label-hint">Adjust thresholds based on ATR</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.dynamicThresholds?.enabled ? 'active' : ''}`}
              onClick={handleDynamicThresholdToggle}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          {activeThresholds && (
            <div className="gs-atr-status">
              <div className="gs-atr-row">
                <span>Current ATR:</span>
                <span className="gs-atr-value">{activeThresholds.currentATR.toFixed(1)}</span>
                {activeThresholds.atrLabel && (
                  <span
                    className={`gs-atr-label gs-atr-${activeThresholds.atrLabel.toLowerCase().replace(' ', '-')}`}
                  >
                    {activeThresholds.atrLabel}
                  </span>
                )}
              </div>
              <div className="gs-atr-row">
                <span>Active Multiplier:</span>
                <span className="gs-atr-value">{activeThresholds.volumeMultiplier.toFixed(1)}x</span>
              </div>
              <div className="gs-atr-row">
                <span>Active Min Ratio:</span>
                <span className="gs-atr-value">{activeThresholds.minVolumeRatio}x</span>
              </div>
            </div>
          )}

          {pinningVolumeSettings.dynamicThresholds?.enabled && (
            <div className="gs-atr-mappings">
              <div className="gs-atr-mappings-header">
                <span>ATR Range</span>
                <span>Mult</span>
                <span>Ratio</span>
              </div>
              {pinningVolumeSettings.dynamicThresholds?.mappings.map((mapping, index) => (
                <div key={index} className="gs-atr-mapping-row">
                  <span className="gs-atr-range">
                    {mapping.atrMin}-{mapping.atrMax === 999 ? '+' : mapping.atrMax}
                  </span>
                  <input
                    type="number"
                    min="0.5"
                    max="5"
                    step="0.1"
                    value={mapping.volumeMultiplier}
                    onChange={(e) =>
                      handleMappingChange(index, 'volumeMultiplier', Number(e.target.value))
                    }
                    className="gs-atr-input"
                  />
                  <input
                    type="number"
                    min="1"
                    max="25"
                    step="1"
                    value={mapping.minVolumeRatio}
                    onChange={(e) =>
                      handleMappingChange(index, 'minVolumeRatio', Number(e.target.value))
                    }
                    className="gs-atr-input"
                  />
                </div>
              ))}
            </div>
          )}

          <button className="gs-optimization-btn" onClick={() => setShowOptimizationModal(true)}>
            <span>⚙</span>
            Run Optimization
          </button>
        </div>

        {/* Volume Settings Section */}
        <div
          className={`gs-section ${pinningVolumeSettings.dynamicThresholds?.enabled ? 'dimmed' : ''}`}
        >
          <div className="gs-section-header">
            <span className="gs-section-icon purple">V</span>
            <span className="gs-section-title">
              Volume Detection{' '}
              {pinningVolumeSettings.dynamicThresholds?.enabled ? '(Static - Fallback)' : ''}
            </span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Volume Multiplier</span>
              <span className="gs-value purple">
                {pinningVolumeSettings.volumeMultiplier.toFixed(1)}x
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="50"
              step="0.5"
              value={pinningVolumeSettings.volumeMultiplier}
              onChange={(e) => handlePVSliderChange('volumeMultiplier', Number(e.target.value))}
              className="gs-slider purple"
              disabled={pinningVolumeSettings.dynamicThresholds?.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Volume Lookback</span>
              <span className="gs-value purple">{pinningVolumeSettings.volumeLookback} bars</span>
            </div>
            <input
              type="range"
              min="5"
              max="50"
              value={pinningVolumeSettings.volumeLookback}
              onChange={(e) => handlePVSliderChange('volumeLookback', Number(e.target.value))}
              className="gs-slider purple"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Min Volume Ratio</span>
              <span className="gs-value purple">{pinningVolumeSettings.minVolumeRatio}x</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              step="0.5"
              value={pinningVolumeSettings.minVolumeRatio}
              onChange={(e) => handlePVSliderChange('minVolumeRatio', Number(e.target.value))}
              className="gs-slider purple"
              disabled={pinningVolumeSettings.dynamicThresholds?.enabled}
            />
          </div>
        </div>

        {/* Reversal Detection Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon orange">R</span>
            <span className="gs-section-title">Reversal Detection</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Min Wick %</span>
              <span className="gs-value orange">{pinningVolumeSettings.minWickPercentage}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="70"
              value={pinningVolumeSettings.minWickPercentage}
              onChange={(e) => handlePVSliderChange('minWickPercentage', Number(e.target.value))}
              className="gs-slider orange"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Prior Move Lookback</span>
              <span className="gs-value orange">{pinningVolumeSettings.priorMoveLookback} bars</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={pinningVolumeSettings.priorMoveLookback}
              onChange={(e) => handlePVSliderChange('priorMoveLookback', Number(e.target.value))}
              className="gs-slider orange"
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Require Aligned Wick</span>
              <span className="gs-label-hint">Wick must match reversal direction</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.requireAlignedWick ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('requireAlignedWick')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Use Wick Direction</span>
              <span className="gs-label-hint">Instead of prior move</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.useWickDirection ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('useWickDirection')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>
        </div>

        {/* Skip Conditions Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">S</span>
            <span className="gs-section-title">Skip Conditions</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Skip Low ATR</span>
              <span className="gs-label-hint">Low volatility</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.skipIfLowATR ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('skipIfLowATR')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>ATR Min Threshold</span>
              <span className="gs-value cyan">{pinningVolumeSettings.atrMinThreshold} pts</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={pinningVolumeSettings.atrMinThreshold}
              onChange={(e) => handlePVSliderChange('atrMinThreshold', Number(e.target.value))}
              className="gs-slider cyan"
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Skip at HOD</span>
              <span className="gs-label-hint">High of Day</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.skipIfHOD ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('skipIfHOD')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Skip at LOD</span>
              <span className="gs-label-hint">Low of Day</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.skipIfLOD ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('skipIfLOD')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>HOD/LOD Range</span>
              <span className="gs-value yellow">{pinningVolumeSettings.hodLodRangePoints} pts</span>
            </div>
            <input
              type="range"
              min="1"
              max="50"
              value={pinningVolumeSettings.hodLodRangePoints}
              onChange={(e) => handlePVSliderChange('hodLodRangePoints', Number(e.target.value))}
              className="gs-slider yellow"
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Skip Small Candles</span>
              <span className="gs-label-hint">Min candle length</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.skipIfSmallCandle ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('skipIfSmallCandle')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Min Candle Length</span>
              <span className="gs-value cyan">{pinningVolumeSettings.minCandleLengthPoints} pts</span>
            </div>
            <input
              type="range"
              min="1"
              max="30"
              value={pinningVolumeSettings.minCandleLengthPoints}
              onChange={(e) =>
                handlePVSliderChange('minCandleLengthPoints', Number(e.target.value))
              }
              className="gs-slider cyan"
            />
          </div>
        </div>

        {/* Trade Execution Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon red">E</span>
            <span className="gs-section-title">Execution</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Auto Trade</span>
              <span className="gs-label-hint">Execute signals automatically</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.tradeExecutionEnabled ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('tradeExecutionEnabled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>
        </div>

        {/* Confluence Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon green">C</span>
            <span className="gs-section-title">Confluence</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Track Correlated Symbols</span>
              <span className="gs-label-hint">Score signals by volume on related contracts (NQ, MNQ, ES, MES)</span>
            </div>
            <button
              className={`gs-toggle ${pinningVolumeSettings.confluenceEnabled ? 'active' : ''}`}
              onClick={() => handlePVToggleChange('confluenceEnabled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          {pinningVolumeSettings.confluenceEnabled && (
            <div className="gs-confluence-info">
              <div className="gs-confluence-info-text">
                Checks NQ, MNQ, ES, MES for volume spikes on same bar and bar-1.
                Score shows on markers (0-100 pts).
              </div>
              <div className="gs-confluence-scoring">
                <div className="gs-confluence-score-item">
                  <span className="gs-confluence-score-label">Same bar match:</span>
                  <span className="gs-confluence-score-value">+30 pts</span>
                </div>
                <div className="gs-confluence-score-item">
                  <span className="gs-confluence-score-label">Bar-1 match:</span>
                  <span className="gs-confluence-score-value">+20 pts</span>
                </div>
                <div className="gs-confluence-score-item">
                  <span className="gs-confluence-score-label">Full contract bonus:</span>
                  <span className="gs-confluence-score-value">+10 pts</span>
                </div>
                <div className="gs-confluence-score-item">
                  <span className="gs-confluence-score-label">Cross-index (NQ+ES):</span>
                  <span className="gs-confluence-score-value">+15 pts</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }


  // ICT (FVG/IFVG) tab
  if (strategyTab === 'ict') {
    const allTimeframes: ICTTimeframe[] = ['2m', '5m', '15m', '1h', '4h'];

    return (
      <div className="gs-tab-content">
        {/* Strategy Enable Toggle + Recalculate */}
        <div className="gs-section gs-strategy-enable">
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable ICT Strategy</span>
              <span className="gs-label-hint">Show FVG/IFVG zones on chart</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={ictSettings.enabled}
                onChange={() => handleICTToggleChange('enabled')}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={handleRecalculateICT}
              disabled={isRecalculating}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate Zones'}
            </button>
            {recalculateResult && (
              <span className={`gs-recalculate-result ${recalculateResult.type}`}>
                {recalculateResult.message}
              </span>
            )}
          </div>
        </div>

        {/* FVG Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">F</span>
            <span className="gs-section-title">Fair Value Gaps (FVG)</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Enable FVG Detection</span>
              <span className="gs-label-hint">Detect price gaps in candles</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.fvgEnabled ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('fvgEnabled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Timeframes</span>
              <span className="gs-label-hint">Select active timeframes</span>
            </div>
            <div className="gs-timeframe-buttons">
              {allTimeframes.map(tf => (
                <button
                  key={tf}
                  className={`gs-timeframe-btn ${ictSettings.fvgTimeframes.includes(tf) ? 'active' : ''}`}
                  onClick={() => handleICTTimeframeToggle('fvgTimeframes', tf)}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {ictSettings.fvgTimeframes.length > 0 && (
            <div className="gs-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
              <div className="gs-label">
                <span>Lookback Periods</span>
                <span className="gs-label-hint">Bars to scan per timeframe</span>
              </div>
              {ictSettings.fvgTimeframes.map(tf => (
                <div key={tf} className="gs-row" style={{ paddingLeft: '8px' }}>
                  <div className="gs-label">
                    <span>{tf.toUpperCase()} Lookback</span>
                    <span className="gs-value">{ictSettings.fvgLookbackByTimeframe[tf] ?? 30} bars</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    step="5"
                    value={ictSettings.fvgLookbackByTimeframe[tf] ?? 30}
                    onChange={(e) => handleLookbackChange('fvgLookbackByTimeframe', tf, Number(e.target.value))}
                    className="gs-slider"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="gs-row">
            <div className="gs-label">
              <span>Min Gap Size</span>
              <span className="gs-value">{ictSettings.fvgMinGapAtrMultiple.toFixed(2)} × ATR</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.05"
              value={ictSettings.fvgMinGapAtrMultiple}
              onChange={(e) => handleICTSliderChange('fvgMinGapAtrMultiple', Number(e.target.value))}
              className="gs-slider"
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show FVG zones on chart</span>
              <span className="gs-label-hint">Render active FVG zones</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.showFvgOnChart ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('showFvgOnChart')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show Filled Zones</span>
              <span className="gs-label-hint">Display zones that have been filled</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.fvgShowFilled ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('fvgShowFilled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Unfilled Opacity</span>
              <span className="gs-value">{Math.round(ictSettings.fvgUnfilledOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.05"
              value={ictSettings.fvgUnfilledOpacity}
              onChange={(e) => handleICTSliderChange('fvgUnfilledOpacity', Number(e.target.value))}
              className="gs-slider"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Filled Opacity</span>
              <span className="gs-value">{Math.round(ictSettings.fvgFilledOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.05"
              max="0.5"
              step="0.05"
              value={ictSettings.fvgFilledOpacity}
              onChange={(e) => handleICTSliderChange('fvgFilledOpacity', Number(e.target.value))}
              className="gs-slider"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Bullish Color</span>
            </div>
            <input
              type="color"
              value={ictSettings.fvgBullishColor}
              onChange={(e) => handleICTColorChange('fvgBullishColor', e.target.value)}
              className="gs-color-picker"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Bearish Color</span>
            </div>
            <input
              type="color"
              value={ictSettings.fvgBearishColor}
              onChange={(e) => handleICTColorChange('fvgBearishColor', e.target.value)}
              className="gs-color-picker"
            />
          </div>
        </div>

        {/* IFVG Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">I</span>
            <span className="gs-section-title">Inverse FVG (IFVG)</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Enable IFVG Detection</span>
              <span className="gs-label-hint">Track filled gaps that become inverse zones</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.ifvgEnabled ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('ifvgEnabled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show IFVG zones on chart</span>
              <span className="gs-label-hint">Render inverted FVG zones</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.showIfvgOnChart ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('showIfvgOnChart')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show invalidated IFVG zones</span>
              <span className="gs-label-hint">Include IFVGs that have been closed through (invalidated)</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.ifvgShowFilled ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('ifvgShowFilled')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show timeframe badges on zones</span>
              <span className="gs-label-hint">Display 5M (24m) labels on each zone</span>
            </div>
            <button
              className={`gs-toggle ${ictSettings.showZoneBadges ? 'active' : ''}`}
              onClick={() => handleICTToggleChange('showZoneBadges')}
            >
              <span className="gs-toggle-knob" />
            </button>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Timeframes</span>
              <span className="gs-label-hint">Select active timeframes</span>
            </div>
            <div className="gs-timeframe-buttons">
              {allTimeframes.map(tf => (
                <button
                  key={tf}
                  className={`gs-timeframe-btn ${ictSettings.ifvgTimeframes.includes(tf) ? 'active' : ''}`}
                  onClick={() => handleICTTimeframeToggle('ifvgTimeframes', tf)}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {ictSettings.ifvgTimeframes.length > 0 && (
            <div className="gs-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
              <div className="gs-label">
                <span>Lookback Periods</span>
                <span className="gs-label-hint">Bars to scan per timeframe</span>
              </div>
              {ictSettings.ifvgTimeframes.map(tf => (
                <div key={tf} className="gs-row" style={{ paddingLeft: '8px' }}>
                  <div className="gs-label">
                    <span>{tf.toUpperCase()} Lookback</span>
                    <span className="gs-value">{ictSettings.ifvgLookbackByTimeframe[tf] ?? 30} bars</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    step="5"
                    value={ictSettings.ifvgLookbackByTimeframe[tf] ?? 30}
                    onChange={(e) => handleLookbackChange('ifvgLookbackByTimeframe', tf, Number(e.target.value))}
                    className="gs-slider"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="gs-row">
            <div className="gs-label">
              <span>Min Gap Size</span>
              <span className="gs-value">{ictSettings.ifvgMinGapAtrMultiple.toFixed(2)} × ATR</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.05"
              value={ictSettings.ifvgMinGapAtrMultiple}
              onChange={(e) => handleICTSliderChange('ifvgMinGapAtrMultiple', Number(e.target.value))}
              className="gs-slider"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Bullish Color</span>
            </div>
            <input
              type="color"
              value={ictSettings.ifvgBullishColor}
              onChange={(e) => handleICTColorChange('ifvgBullishColor', e.target.value)}
              className="gs-color-picker"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Bearish Color</span>
            </div>
            <input
              type="color"
              value={ictSettings.ifvgBearishColor}
              onChange={(e) => handleICTColorChange('ifvgBearishColor', e.target.value)}
              className="gs-color-picker"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Unfilled Opacity</span>
              <span className="gs-value">{Math.round(ictSettings.ifvgUnfilledOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={ictSettings.ifvgUnfilledOpacity}
              onChange={(e) => handleICTSliderChange('ifvgUnfilledOpacity', Number(e.target.value))}
              className="gs-slider"
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Filled Opacity</span>
              <span className="gs-value">{Math.round(ictSettings.ifvgFilledOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={ictSettings.ifvgFilledOpacity}
              onChange={(e) => handleICTSliderChange('ifvgFilledOpacity', Number(e.target.value))}
              className="gs-slider"
            />
          </div>
        </div>
      </div>
    );
  }

  // Volume Profile tab
  if (strategyTab === 'volume-profile') {
    // Helper to format session type for display
    const formatSessionType = (st: SessionType): string => {
      if (st.type === 'rth') return 'RTH';
      if (st.type === 'eth') return 'ETH';
      if (st.type === 'rolling') return `Rolling:${st.value}`;
      if (st.type === 'custom') return 'Custom';
      return 'Unknown';
    };

    // Helper to parse session type from select value
    const parseSessionType = (value: string): SessionType => {
      if (value === 'RTH') return { type: 'rth' };
      if (value === 'ETH') return { type: 'eth' };
      if (value.startsWith('Rolling:')) {
        return { type: 'rolling', value: parseInt(value.split(':')[1]) };
      }
      return { type: 'rolling', value: 30 }; // default
    };

    return (
      <div className="gs-tab-content">
        {/* Strategy Enable Toggle */}
        <div className="gs-section gs-strategy-enable">
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Volume Profile</span>
              <span className="gs-label-hint">Show volume profile visualization on chart</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={vpSettings.enabled}
                onChange={(e) => setVpSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked,
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Session Settings */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">S</span>
            <span className="gs-section-title">Session Settings</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span>Session Type</span>
              <span className="gs-label-hint">How to aggregate volume data</span>
            </div>
            <select
              className="gs-select"
              value={formatSessionType(vpSettings.sessionType)}
              onChange={(e) => setVpSettings(prev => ({
                ...prev,
                sessionType: parseSessionType(e.target.value),
              }))}
            >
              <option value="RTH">RTH (Regular Trading Hours)</option>
              <option value="ETH">ETH (Extended Hours)</option>
              <option value="Rolling:30">Rolling 30 min</option>
              <option value="Rolling:60">Rolling 60 min</option>
              <option value="Rolling:120">Rolling 2 hours</option>
            </select>
          </div>
        </div>

        {/* Display Options */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">D</span>
            <span className="gs-section-title">Display Options</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show Delta (Buy/Sell Coloring)</span>
              <span className="gs-label-hint">Color bars by buy/sell volume</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={vpSettings.showDelta}
                onChange={(e) => setVpSettings(prev => ({
                  ...prev,
                  showDelta: e.target.checked,
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show Cumulative Delta Line</span>
              <span className="gs-label-hint">Display running delta as line overlay</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={vpSettings.showCumulativeDelta}
                onChange={(e) => setVpSettings(prev => ({
                  ...prev,
                  showCumulativeDelta: e.target.checked,
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Highlight POC (Point of Control)</span>
              <span className="gs-label-hint">Emphasize highest volume price level</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={vpSettings.showPoc}
                onChange={(e) => setVpSettings(prev => ({
                  ...prev,
                  showPoc: e.target.checked,
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span>Show Value Area</span>
              <span className="gs-label-hint">Highlight value area zone</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={vpSettings.showValueArea}
                onChange={(e) => setVpSettings(prev => ({
                  ...prev,
                  showValueArea: e.target.checked,
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {vpSettings.showValueArea && (
            <>
              <div className="gs-row">
                <div className="gs-label">
                  <span>Value Area %</span>
                  <span className="gs-value">{vpSettings.valueAreaPercent}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="90"
                  step="5"
                  value={vpSettings.valueAreaPercent}
                  onChange={(e) => setVpSettings(prev => ({
                    ...prev,
                    valueAreaPercent: parseInt(e.target.value),
                  }))}
                  className="gs-slider"
                />
              </div>

              <div className="gs-row gs-toggle-row">
                <div className="gs-label">
                  <span>Show VAH/VAL Lines</span>
                  <span className="gs-label-hint">Draw lines at Value Area High/Low</span>
                </div>
                <label className="gs-toggle-switch">
                  <input
                    type="checkbox"
                    checked={vpSettings.showVahVal ?? true}
                    onChange={(e) => setVpSettings(prev => ({
                      ...prev,
                      showVahVal: e.target.checked,
                    }))}
                  />
                  <span className="gs-toggle-slider"></span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }


  // Donchian Channels tab
  if (strategyTab === 'donchian') {
    return (
      <div className="gs-tab-content">
        {/* Strategy Enable Toggle */}
        <div className="gs-section gs-strategy-enable">
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Donchian Channels</span>
              <span className="gs-label-hint">Generate signals on Donchian Channel breakouts</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={donchianSettings.enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDonchianSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={async () => {
                try {
                  await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/donchian-markers/recalculate?days=3`, { method: 'POST' });
                  alert('Donchian markers recalculated');
                } catch (err) {
                  alert('Recalculate failed');
                }
              }}
            >
              Recalculate Markers
            </button>
          </div>
        </div>

        {/* Lookback Periods */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">~</span>
            <span className="gs-section-title">Lookback Periods</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Entry Lookback</span>
              <span className="gs-label-hint">Fast channel for breakout entry (default: 20)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="10"
              max="50"
              step="1"
              value={donchianSettings.entryLookback}
              onChange={(e) => setDonchianSettings(prev => ({
                ...prev,
                entryLookback: parseInt(e.target.value) || 20
              }))}
              disabled={!donchianSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Trend Lookback</span>
              <span className="gs-label-hint">Slow channel for trend filter (default: 55)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="30"
              max="120"
              step="1"
              value={donchianSettings.trendLookback}
              onChange={(e) => setDonchianSettings(prev => ({
                ...prev,
                trendLookback: parseInt(e.target.value) || 55
              }))}
              disabled={!donchianSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Exit Lookback</span>
              <span className="gs-label-hint">Channel for exit signals (default: 10)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="5"
              max="30"
              step="1"
              value={donchianSettings.exitLookback}
              onChange={(e) => setDonchianSettings(prev => ({
                ...prev,
                exitLookback: parseInt(e.target.value) || 10
              }))}
              disabled={!donchianSettings.enabled}
            />
          </div>
        </div>

        {/* Volume Filter */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">V</span>
            <span className="gs-section-title">Volume Filter</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Volume Multiplier</span>
              <span className="gs-label-hint">Required volume vs average (default: 1.5x)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="1.0"
              max="3.0"
              step="0.1"
              value={donchianSettings.volumeMultiplier}
              onChange={(e) => setDonchianSettings(prev => ({
                ...prev,
                volumeMultiplier: parseFloat(e.target.value) || 1.5
              }))}
              disabled={!donchianSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Volume Lookback</span>
              <span className="gs-label-hint">Bars for average volume calculation (default: 10)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="5"
              max="30"
              step="1"
              value={donchianSettings.volumeLookback}
              onChange={(e) => setDonchianSettings(prev => ({
                ...prev,
                volumeLookback: parseInt(e.target.value) || 10
              }))}
              disabled={!donchianSettings.enabled}
            />
          </div>
        </div>

        {/* Trend Filter */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">T</span>
            <span className="gs-section-title">Trend Filter</span>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Require Trend Filter</span>
              <span className="gs-label-hint">Only signal when price is above/below trend channel</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={donchianSettings.requireTrendFilter}
                onChange={(e) => setDonchianSettings(prev => ({
                  ...prev,
                  requireTrendFilter: e.target.checked
                }))}
                disabled={!donchianSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Save Button */}
        {donchianHasChanges && (
          <div className="gs-row gs-save-row">
            <button
              className="gs-btn gs-btn-primary"
              onClick={async () => {
                try {
                  await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/donchian-settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(donchianSettings),
                  });
                  setOriginalDonchianSettings(donchianSettings);
                  useDonchianSettingsStore.getState().setSettings(donchianSettings);
                } catch (err) {
                  console.error('Failed to save donchian settings:', err);
                  alert('Failed to save.');
                }
              }}
              disabled={isSyncing}
            >
              {isSyncing ? 'Saving...' : 'Save Donchian Settings'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Break of Structure (BOS) tab
  if (strategyTab === 'break-of-structure') {
    return (
      <div className="gs-tab-content">
        {/* Strategy Enable Toggle */}
        <div className="gs-section gs-strategy-enable">
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Break of Structure</span>
              <span className="gs-label-hint">Mark BOS / CHoCH structure breaks on swing pivots</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={bosSettings.enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBosSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={async () => {
                try {
                  await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-markers/recalculate?days=3`, { method: 'POST' });
                  alert('Break of Structure markers recalculated');
                } catch (err) {
                  alert('Recalculate failed');
                }
              }}
            >
              Recalculate Markers
            </button>
          </div>
        </div>

        {/* Detection */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">~</span>
            <span className="gs-section-title">Detection</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Swing Lookback</span>
              <span className="gs-label-hint">Fractal pivot strength — bars each side (default: 5)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="1"
              step="1"
              value={bosSettings.swingLookback}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                swingLookback: parseInt(e.target.value) || 5
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Min Break Points</span>
              <span className="gs-label-hint">Buffer (points) price must exceed level by (default: 0)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="0"
              step="0.25"
              value={bosSettings.minBreakPoints}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                minBreakPoints: parseFloat(e.target.value) || 0
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Show CHoCH (reversal breaks)</span>
              <span className="gs-label-hint">Emit Change of Character markers on trend reversals</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={bosSettings.showChoch}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  showChoch: e.target.checked
                }))}
                disabled={!bosSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Grade breaks (A+ / A / B+ / B)</span>
              <span className="gs-label-hint">Score each break and show its grade on the chart label</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={bosSettings.gradingEnabled}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  gradingEnabled: e.target.checked
                }))}
                disabled={!bosSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Retest Window (bars)</span>
              <span className="gs-label-hint">Bars to watch for a retest before finalizing the grade (default: 10)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="0"
              step="1"
              value={bosSettings.retestWindowBars}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                retestWindowBars: parseInt(e.target.value) || 0
              }))}
              disabled={!bosSettings.enabled || !bosSettings.gradingEnabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Grade Thresholds (A+ / A / B+)</span>
              <span className="gs-label-hint">Minimum 0-100 score for each grade; below B+ is B (default: 85 / 70 / 55)</span>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="number"
                className="gs-input gs-input-small"
                min="0"
                max="100"
                step="1"
                title="A+ minimum"
                value={bosSettings.gradeBands.aPlusMin}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  gradeBands: { ...prev.gradeBands, aPlusMin: parseInt(e.target.value) || 0 }
                }))}
                disabled={!bosSettings.enabled || !bosSettings.gradingEnabled}
              />
              <input
                type="number"
                className="gs-input gs-input-small"
                min="0"
                max="100"
                step="1"
                title="A minimum"
                value={bosSettings.gradeBands.aMin}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  gradeBands: { ...prev.gradeBands, aMin: parseInt(e.target.value) || 0 }
                }))}
                disabled={!bosSettings.enabled || !bosSettings.gradingEnabled}
              />
              <input
                type="number"
                className="gs-input gs-input-small"
                min="0"
                max="100"
                step="1"
                title="B+ minimum"
                value={bosSettings.gradeBands.bPlusMin}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  gradeBands: { ...prev.gradeBands, bPlusMin: parseInt(e.target.value) || 0 }
                }))}
                disabled={!bosSettings.enabled || !bosSettings.gradingEnabled}
              />
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">C</span>
            <span className="gs-section-title">Appearance</span>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Bullish Color</span>
              <span className="gs-label-hint">Color for bullish BOS markers/lines</span>
            </div>
            <input
              type="color"
              className="gs-input gs-input-color"
              value={bosSettings.bullColor}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                bullColor: e.target.value
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Bearish Color</span>
              <span className="gs-label-hint">Color for bearish BOS markers/lines</span>
            </div>
            <input
              type="color"
              className="gs-input gs-input-color"
              value={bosSettings.bearColor}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                bearColor: e.target.value
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">CHoCH Color</span>
              <span className="gs-label-hint">Color for CHoCH (reversal) markers/lines</span>
            </div>
            <input
              type="color"
              className="gs-input gs-input-color"
              value={bosSettings.chochColor}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                chochColor: e.target.value
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Line Style</span>
              <span className="gs-label-hint">Style of the break level line</span>
            </div>
            <select
              className="gs-input gs-input-small"
              value={bosSettings.lineStyle}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                lineStyle: e.target.value as 'solid' | 'dashed'
              }))}
              disabled={!bosSettings.enabled}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </select>
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Line Width</span>
              <span className="gs-label-hint">Width of the level line in px (default: 2)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="1"
              max="6"
              step="1"
              value={bosSettings.lineWidth}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                lineWidth: parseInt(e.target.value) || 2
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Line Length (bars)</span>
              <span className="gs-label-hint">Max bars the level line extends (default: 10)</span>
            </div>
            <input
              type="number"
              className="gs-input gs-input-small"
              min="1"
              step="1"
              value={bosSettings.lineLengthBars}
              onChange={(e) => setBosSettings(prev => ({
                ...prev,
                lineLengthBars: parseInt(e.target.value) || 10
              }))}
              disabled={!bosSettings.enabled}
            />
          </div>

          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Show Label</span>
              <span className="gs-label-hint">Draw the "BOS" / "CHoCH" label on the marker</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={bosSettings.showLabel}
                onChange={(e) => setBosSettings(prev => ({
                  ...prev,
                  showLabel: e.target.checked
                }))}
                disabled={!bosSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Reset to defaults */}
        <div className="gs-row gs-recalculate-row">
          <button
            className="gs-btn gs-btn-recalculate"
            onClick={() => setBosSettings(DEFAULT_BOS_SETTINGS)}
            disabled={!bosHasChanges && JSON.stringify(bosSettings) === JSON.stringify(DEFAULT_BOS_SETTINGS)}
          >
            Reset to defaults
          </button>
        </div>

        {/* Save Button */}
        {bosHasChanges && (
          <div className="gs-row gs-save-row">
            <button
              className="gs-btn gs-btn-primary"
              onClick={async () => {
                try {
                  const payload = { ...bosSettings, updatedAt: Date.now() };
                  await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                  });
                  await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-markers/recalculate?days=3`, { method: 'POST' });
                  setBosSettings(payload);
                  setOriginalBosSettings(payload);
                  useSettingsStore.getState().setBosSettings(payload);
                } catch (err) {
                  console.error('Failed to save BOS settings:', err);
                  alert('Failed to save.');
                }
              }}
              disabled={isSyncing}
            >
              {isSyncing ? 'Saving...' : 'Save Break of Structure Settings'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Trailing Pullback tab
  if (strategyTab === 'trailing-pullback') {
    return (
      <div className="gs-tab-content">
        {/* Trailing Candle Overlay Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">📊</span>
            <span className="gs-section-title">Trailing Candle Overlay</span>
          </div>
          <p className="gs-section-description">
            Display a mini-candle inside each bar representing the final portion of the candle period.
          </p>

          {/* Enable/Disable Toggle */}
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Trailing Candle</span>
              <span className="gs-label-hint">Show trailing OHLC overlay on chart</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={trailingSettings.enabled}
                onChange={(e) => setTrailingSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {/* Duration Dropdown */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Trailing Duration</span>
              <span className="gs-label-hint">How many seconds at the end of each candle to track</span>
            </div>
            <select
              className="gs-select"
              value={trailingSettings.durationSeconds}
              onChange={(e) => setTrailingSettings(prev => ({
                ...prev,
                durationSeconds: parseInt(e.target.value)
              }))}
              disabled={!trailingSettings.enabled}
            >
              <option value={15}>15 seconds</option>
              <option value={30}>30 seconds</option>
              <option value={45}>45 seconds</option>
              <option value={60}>60 seconds</option>
            </select>
          </div>

          {/* Save Button */}
          {trailingHasChanges && (
            <div className="gs-row gs-save-row">
              <button
                className="gs-btn gs-btn-primary"
                onClick={async () => {
                  try {
                    // Save to backend
                    await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-candle-settings`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        enabled: trailingSettings.enabled,
                        duration_seconds: trailingSettings.durationSeconds,
                      }),
                    });

                    // If duration changed, prompt for recalculate
                    if (trailingSettings.durationSeconds !== originalTrailingSettings.durationSeconds) {
                      const shouldRecalculate = window.confirm(
                        'Duration changed. Recalculate historical trailing data?\n\n' +
                        'This will re-process historical candles with the new duration. ' +
                        'It may take a moment.'
                      );

                      if (shouldRecalculate) {
                        await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-candles/recalculate`, {
                          method: 'POST',
                        });
                      }
                    }

                    // Update original to match current
                    setOriginalTrailingSettings(trailingSettings);

                    // Update local store
                    useSettingsStore.getState().setTrailingCandleSettings(trailingSettings);
                  } catch (err) {
                    console.error('Failed to save trailing candle settings:', err);
                    alert('Failed to save settings. Please try again.');
                  }
                }}
                disabled={isSyncing}
              >
                {isSyncing ? 'Saving...' : 'Save Trailing Settings'}
              </button>
            </div>
          )}
        </div>

        {/* Trailing Pullback Strategy Section */}
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">📈</span>
            <span className="gs-section-title">Trailing Pullback Strategy</span>
          </div>
          <p className="gs-section-description">
            Generate signals when trailing candles show healthy pullback patterns (opposite direction to main candle).
          </p>

          {/* Enable/Disable Toggle */}
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Strategy</span>
              <span className="gs-label-hint">Generate trailing pullback signals</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={trailingPullbackSettings.enabled}
                onChange={(e) => setTrailingPullbackSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {/* Confidence Threshold Slider */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Confidence Threshold</span>
              <span className="gs-label-hint">Minimum confidence to emit signal (0-100)</span>
            </div>
            <div className="gs-slider-container">
              <input
                type="range"
                className="gs-slider"
                min="0"
                max="100"
                value={trailingPullbackSettings.confidenceThreshold}
                onChange={(e) => setTrailingPullbackSettings(prev => ({
                  ...prev,
                  confidenceThreshold: parseInt(e.target.value)
                }))}
                disabled={!trailingPullbackSettings.enabled}
              />
              <span className="gs-slider-value">{trailingPullbackSettings.confidenceThreshold}</span>
            </div>
          </div>

          {/* Include Continuation Toggle */}
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Include Continuation</span>
              <span className="gs-label-hint">Include same-direction trailing patterns</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={trailingPullbackSettings.includeContinuation}
                onChange={(e) => setTrailingPullbackSettings(prev => ({
                  ...prev,
                  includeContinuation: e.target.checked
                }))}
                disabled={!trailingPullbackSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {/* Require WCC Alignment Toggle */}
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Require WCC Alignment</span>
              <span className="gs-label-hint">Only signal when matching WCC signal exists</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={trailingPullbackSettings.requireWccAlignment}
                onChange={(e) => setTrailingPullbackSettings(prev => ({
                  ...prev,
                  requireWccAlignment: e.target.checked
                }))}
                disabled={!trailingPullbackSettings.enabled}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {/* Min Trailing Body % Slider */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Min Trailing Body %</span>
              <span className="gs-label-hint">Minimum trailing body as % of main candle (1-30)</span>
            </div>
            <div className="gs-slider-container">
              <input
                type="range"
                className="gs-slider"
                min="1"
                max="30"
                value={trailingPullbackSettings.minTrailingBodyPct}
                onChange={(e) => setTrailingPullbackSettings(prev => ({
                  ...prev,
                  minTrailingBodyPct: parseInt(e.target.value)
                }))}
                disabled={!trailingPullbackSettings.enabled}
              />
              <span className="gs-slider-value">{trailingPullbackSettings.minTrailingBodyPct}%</span>
            </div>
          </div>

          {/* Save Button */}
          {trailingPullbackHasChanges && (
            <div className="gs-row gs-save-row">
              <button
                className="gs-btn gs-btn-primary"
                onClick={async () => {
                  try {
                    // Save to backend
                    await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        enabled: trailingPullbackSettings.enabled,
                        confidenceThreshold: trailingPullbackSettings.confidenceThreshold,
                        includeContinuation: trailingPullbackSettings.includeContinuation,
                        requireWccAlignment: trailingPullbackSettings.requireWccAlignment,
                        minTrailingBodyPct: trailingPullbackSettings.minTrailingBodyPct,
                        updatedAt: Date.now(),
                      }),
                    });

                    // Update original to match current
                    setOriginalTrailingPullbackSettings(trailingPullbackSettings);

                    // Update local store
                    useSettingsStore.getState().setTrailingPullbackSettings(trailingPullbackSettings);
                  } catch (err) {
                    console.error('Failed to save trailing pullback settings:', err);
                    alert('Failed to save settings. Please try again.');
                  }
                }}
                disabled={isSyncing}
              >
                {isSyncing ? 'Saving...' : 'Save Trailing Pullback Settings'}
              </button>
            </div>
          )}

          {/* Recalculate Row */}
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={handleRecalculateTrailingPullback}
              disabled={isRecalculating}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate Markers'}
            </button>
            {recalculateResult && (
              <span className={`gs-recalculate-result ${recalculateResult.type}`}>
                {recalculateResult.message}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Magnet Candle tab
  if (strategyTab === 'magnet-candle') {
    // Per-symbol effective settings (like WCC localSettings pattern)
    const effectiveMcSettings = mcSettingsBySymbol[symbolTab] || magnetCandleSettings;
    const setEffectiveMcSettings = (newSettings: MagnetCandleSettings | ((prev: MagnetCandleSettings) => MagnetCandleSettings)) => {
      setMcSettingsBySymbol(prev => {
        const current = prev[symbolTab] || magnetCandleSettings;
        return {
          ...prev,
          [symbolTab]: typeof newSettings === 'function' ? newSettings(current) : newSettings
        };
      });
    };
    return (
      <div className="gs-tab-content">
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">🧲</span>
            <span className="gs-section-title">Magnet Candle Strategy</span>
          </div>
          <p className="gs-section-description">
            Detect candles with extreme wicks that act as price magnets — levels where price is likely to return.
          </p>

          {/* Enable/Disable Toggle */}
          <div className="gs-row gs-toggle-row">
            <div className="gs-label">
              <span className="gs-label-main">Enable Strategy</span>
              <span className="gs-label-hint">Generate magnet candle markers</span>
            </div>
            <label className="gs-toggle-switch">
              <input
                type="checkbox"
                checked={effectiveMcSettings.enabled}
                onChange={(e) => setEffectiveMcSettings(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
              />
              <span className="gs-toggle-slider"></span>
            </label>
          </div>

          {/* Wick Threshold Slider */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Wick Threshold %</span>
              <span className="gs-label-hint">Minimum wick percentage to qualify as a magnet (0-25)</span>
            </div>
            <div className="gs-slider-container">
              <input
                type="range"
                className="gs-slider"
                min="0"
                max="25"
                step="0.5"
                value={effectiveMcSettings.wickThreshold}
                onChange={(e) => setEffectiveMcSettings(prev => ({
                  ...prev,
                  wickThreshold: parseFloat(e.target.value)
                }))}
                disabled={!effectiveMcSettings.enabled}
              />
              <span className="gs-slider-value">{effectiveMcSettings.wickThreshold}%</span>
            </div>
          </div>

          {/* Min Body % Slider */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Min Body %</span>
              <span className="gs-label-hint">Minimum body as percentage of candle range (0-100)</span>
            </div>
            <div className="gs-slider-container">
              <input
                type="range"
                className="gs-slider"
                min="0"
                max="100"
                value={effectiveMcSettings.minBodyPct}
                onChange={(e) => setEffectiveMcSettings(prev => ({
                  ...prev,
                  minBodyPct: parseInt(e.target.value)
                }))}
                disabled={!effectiveMcSettings.enabled}
              />
              <span className="gs-slider-value">{effectiveMcSettings.minBodyPct}%</span>
            </div>
          </div>

          {/* Min Range Points */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Min Range Points</span>
              <span className="gs-label-hint">Minimum candle range in points</span>
            </div>
            <input
              type="number"
              className="gs-input"
              min="0"
              step="0.5"
              value={effectiveMcSettings.minRangePoints}
              onChange={(e) => setEffectiveMcSettings(prev => ({
                ...prev,
                minRangePoints: parseFloat(e.target.value) || 0
              }))}
              disabled={!effectiveMcSettings.enabled}
            />
          </div>

          {/* Max Active Magnets */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Max Active Magnets</span>
              <span className="gs-label-hint">Maximum number of active magnet levels</span>
            </div>
            <input
              type="number"
              className="gs-input"
              min="1"
              max="50"
              value={effectiveMcSettings.maxActiveMagnets}
              onChange={(e) => setEffectiveMcSettings(prev => ({
                ...prev,
                maxActiveMagnets: parseInt(e.target.value) || 10
              }))}
              disabled={!effectiveMcSettings.enabled}
            />
          </div>

          {/* Lookback Candles */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Lookback Candles</span>
              <span className="gs-label-hint">Number of candles to scan for magnets</span>
            </div>
            <input
              type="number"
              className="gs-input"
              min="10"
              max="1000"
              value={effectiveMcSettings.lookbackCandles}
              onChange={(e) => setEffectiveMcSettings(prev => ({
                ...prev,
                lookbackCandles: parseInt(e.target.value) || 200
              }))}
              disabled={!effectiveMcSettings.enabled}
            />
          </div>

          {/* Extension Lines - horizontal lines from unbreached magnet levels to the right edge */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Extension Lines</span>
              <span className="gs-label-hint">How many recent unbreached magnets extend a line to the right (0 = off)</span>
            </div>
            <div className="gs-slider-container">
              <input
                type="range"
                className="gs-slider"
                min="0"
                max="10"
                value={effectiveMcSettings.extensionLineCount}
                onChange={(e) => setEffectiveMcSettings(prev => ({
                  ...prev,
                  extensionLineCount: parseInt(e.target.value)
                }))}
                disabled={!effectiveMcSettings.enabled}
              />
              <span className="gs-slider-value">{effectiveMcSettings.extensionLineCount}</span>
            </div>
          </div>

          {/* Save Button */}
          {magnetCandleHasChanges && (
            <div className="gs-row gs-save-row">
              <button
                className="gs-btn gs-btn-primary"
                onClick={async () => {
                  try {
                    const settingsToSave = effectiveMcSettings;
                    const saveUrl = `${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings/${symbolTab}`;
                    const saveResponse = await fetch(saveUrl, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        enabled: settingsToSave.enabled,
                        wickThreshold: settingsToSave.wickThreshold,
                        minBodyPct: settingsToSave.minBodyPct,
                        minRangePoints: settingsToSave.minRangePoints,
                        maxActiveMagnets: settingsToSave.maxActiveMagnets,
                        lookbackCandles: settingsToSave.lookbackCandles,
                        extensionLineCount: settingsToSave.extensionLineCount,
                        updatedAt: Date.now(),
                      }),
                    });
                    if (!saveResponse.ok) throw new Error('Server returned ' + saveResponse.status);

                    setOriginalMcSettingsBySymbol(prev => ({ ...prev, [symbolTab]: settingsToSave }));
                    useSettingsStore.getState().setMagnetCandleForSymbol(symbolTab, settingsToSave);
                  } catch (err) {
                    console.error('Failed to save magnet candle settings:', err);
                    alert('Failed to save settings. Please try again.');
                  }
                }}
                disabled={isSyncing}
              >
                {isSyncing ? 'Saving...' : `Save Magnet Candle Settings (${symbolTab})`}
              </button>
            </div>
          )}

          {/* Recalculate Row */}
          <div className="gs-row gs-recalculate-row">
            <button
              className="gs-btn gs-btn-recalculate"
              onClick={handleRecalculateMagnetCandle}
              disabled={isRecalculating}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate Markers'}
            </button>
            {recalculateResult && (
              <span className={`gs-recalculate-result ${recalculateResult.type}`}>
                {recalculateResult.message}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }


  // SR Proximity Alerts tab
  if (strategyTab === 'sr-alerts') {
    return (
      <div className="gs-tab-content">
        <div className="gs-section">
          <div className="gs-section-header">
            <span className="gs-section-icon">🔔</span>
            <span className="gs-section-title">S/R Proximity Alerts</span>
          </div>

          {/* Master Toggle */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Enabled</span>
              <span className="gs-label-hint">Master toggle for S/R proximity alerts</span>
            </div>
            <input
              type="checkbox"
              className="gs-checkbox"
              checked={srAlertSettings.enabled}
              onChange={(e) => {
                setSrAlertSettings(s => ({ ...s, enabled: e.target.checked, updatedAt: Date.now() }));
                setSrAlertHasChanges(true);
              }}
            />
          </div>

          {/* Alert on Approaching */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Alert on Approaching</span>
              <span className="gs-label-hint">Fire when price approaches a level</span>
            </div>
            <input
              type="checkbox"
              className="gs-checkbox"
              checked={srAlertSettings.alertOnApproaching}
              onChange={(e) => {
                setSrAlertSettings(s => ({ ...s, alertOnApproaching: e.target.checked, updatedAt: Date.now() }));
                setSrAlertHasChanges(true);
              }}
              disabled={!srAlertSettings.enabled}
            />
          </div>

          {/* Alert on Breach */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Alert on Breach</span>
              <span className="gs-label-hint">Fire when price crosses a level</span>
            </div>
            <input
              type="checkbox"
              className="gs-checkbox"
              checked={srAlertSettings.alertOnBreach}
              onChange={(e) => {
                setSrAlertSettings(s => ({ ...s, alertOnBreach: e.target.checked, updatedAt: Date.now() }));
                setSrAlertHasChanges(true);
              }}
              disabled={!srAlertSettings.enabled}
            />
          </div>

          {/* ATR Multiplier */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">ATR Multiplier</span>
              <span className="gs-label-hint">Distance threshold as ATR multiplier</span>
            </div>
            <input
              type="number"
              className="gs-input"
              step="0.1"
              min="0.1"
              max="3.0"
              value={srAlertSettings.atrMultiplier}
              onChange={(e) => {
                setSrAlertSettings(s => ({ ...s, atrMultiplier: parseFloat(e.target.value) || 0.5, updatedAt: Date.now() }));
                setSrAlertHasChanges(true);
              }}
              disabled={!srAlertSettings.enabled}
            />
          </div>

          {/* Cooldown */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Cooldown (minutes)</span>
              <span className="gs-label-hint">Minutes before same level can re-alert</span>
            </div>
            <select
              className="gs-select"
              value={srAlertSettings.cooldownMinutes}
              onChange={(e) => {
                setSrAlertSettings(s => ({ ...s, cooldownMinutes: parseInt(e.target.value), updatedAt: Date.now() }));
                setSrAlertHasChanges(true);
              }}
              disabled={!srAlertSettings.enabled}
            >
              <option value={5}>5 min</option>
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>60 min</option>
            </select>
          </div>

          {/* Alert Timeframes */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Alert Timeframes</span>
              <span className="gs-label-hint">Which S/R timeframes trigger alerts</span>
            </div>
            <div className="gs-timeframe-checkboxes">
              {['5m', '15m', '1h', '4h', 'D', 'W'].map(tf => (
                <label key={tf} className="gs-tf-checkbox">
                  <input
                    type="checkbox"
                    checked={srAlertSettings.alertTimeframes.includes(tf)}
                    onChange={(e) => {
                      setSrAlertSettings(s => ({
                        ...s,
                        alertTimeframes: e.target.checked
                          ? [...s.alertTimeframes, tf]
                          : s.alertTimeframes.filter(t => t !== tf),
                        updatedAt: Date.now(),
                      }));
                      setSrAlertHasChanges(true);
                    }}
                    disabled={!srAlertSettings.enabled}
                  />
                  {tf}
                </label>
              ))}
            </div>
          </div>

          {/* Disabled Symbols */}
          <div className="gs-row">
            <div className="gs-label">
              <span className="gs-label-main">Disabled Symbols</span>
              <span className="gs-label-hint">Comma-separated symbols to skip (e.g., ES, GC)</span>
            </div>
            <input
              type="text"
              className="gs-input"
              value={srAlertSettings.disabledSymbols.join(', ')}
              onChange={(e) => {
                setSrAlertSettings(s => ({
                  ...s,
                  disabledSymbols: e.target.value.split(',').map(v => v.trim()).filter(Boolean),
                  updatedAt: Date.now(),
                }));
                setSrAlertHasChanges(true);
              }}
              disabled={!srAlertSettings.enabled}
              placeholder="ES, GC, NQ"
            />
          </div>
        </div>
      </div>
    );
  }
  return null;
  };

  // Use portal to render modal at document body level, escaping any stacking context
  return createPortal(
    <div className="gs-backdrop" onClick={handleBackdropClick}>
      <div className="gs-modal">
        {/* Mobile Header */}
        <div className="gs-mobile-header">
          <button className="gs-menu-btn" onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}>
            ☰
          </button>
          <h2>Settings</h2>
          <button className="gs-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Sidebar */}
        <div className={`gs-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}>
          <div className="gs-sidebar-header">
            <h2>Settings</h2>
            <button className="gs-close-btn desktop-only" onClick={onClose}>
              ×
            </button>
          </div>

          <nav className="gs-nav">
            <button
              className={`gs-nav-item ${activeSection === 'general' ? 'active' : ''}`}
              onClick={() => handleSectionChange('general')}
            >
              <span className="gs-nav-icon">⚙</span>
              <span>General</span>
            </button>
            <button
              className={`gs-nav-item ${activeSection === 'strategy' ? 'active' : ''}`}
              onClick={() => handleSectionChange('strategy')}
            >
              <span className="gs-nav-icon">📈</span>
              <span>Strategy</span>
            </button>
            <button
              className={`gs-nav-item ${activeSection === 'symbols' ? 'active' : ''}`}
              onClick={() => handleSectionChange('symbols')}
            >
              <span className="gs-nav-icon">$</span>
              <span>Symbols</span>
            </button>
          </nav>
        </div>

        {/* Mobile sidebar overlay */}
        {isMobileSidebarOpen && (
          <div className="gs-sidebar-overlay" onClick={() => setIsMobileSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <div className="gs-main">
          {/* Desktop Header */}
          <div className="gs-header desktop-only">
            <h2>{activeSection === 'general' ? 'General' : activeSection === 'strategy' ? 'Strategy' : 'Symbol'} Settings</h2>
            <button className="gs-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Tabs */}
          {activeSection !== 'symbols' && !(activeSection === 'strategy' && vestSymbol) && (
          <div className="gs-tabs">
            {activeSection === 'general' ? (
              <>
                <button
                  className={`gs-tab ${generalTab === 'appearance' ? 'active' : ''}`}
                  onClick={() => setGeneralTab('appearance')}
                >
                  Appearance
                </button>
                <button
                  className={`gs-tab ${generalTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => setGeneralTab('notifications')}
                >
                  Notifications
                </button>
                <button
                  className={`gs-tab ${generalTab === 'trading' ? 'active' : ''}`}
                  onClick={() => setGeneralTab('trading')}
                >
                  Trading
                </button>
                <button
                  className={`gs-tab ${generalTab === 'account' ? 'active' : ''}`}
                  onClick={() => setGeneralTab('account')}
                >
                  Account
                </button>
              </>
            ) : (
              <>
                <button
                  className={`gs-tab ${strategyTab === 'whole-candle' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('whole-candle')}
                >
                  Whole Candle
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'pinning-volume' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('pinning-volume')}
                >
                  Pinning Volume
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'ict' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('ict')}
                >
                  ICT (FVG)
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'volume-profile' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('volume-profile')}
                >
                  Volume Profile
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'donchian' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('donchian')}
                >
                  Donchian
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'break-of-structure' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('break-of-structure')}
                >
                  Break of Structure
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'trailing-pullback' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('trailing-pullback')}
                >
                  Trailing Pullback
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'magnet-candle' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('magnet-candle')}
                >
                  Magnet Candle
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'sr-alerts' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('sr-alerts')}
                >
                  SR Alerts
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'volume-markers' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('volume-markers')}
                  data-testid="gs-tab-volume-markers"
                >
                  Volume markers 🐋🦈🐱
                </button>
                <button
                  className={`gs-tab ${strategyTab === 'wick-rejection' ? 'active' : ''}`}
                  onClick={() => setStrategyTab('wick-rejection')}
                  data-testid="gs-tab-wick-rejection"
                >
                  Wick rejection 🪝
                </button>
              </>
            )}
          </div>
          )}

          {/* Symbol Sub-tabs (for strategy section only) */}
          {activeSection === 'strategy' && !vestSymbol && strategyTab !== 'ict' && strategyTab !== 'donchian' && strategyTab !== 'break-of-structure' && strategyTab !== 'trailing-pullback' && strategyTab !== 'volume-profile' && strategyTab !== 'volume-markers' && strategyTab !== 'wick-rejection' && (
            <div className="gs-symbol-tabs">
              {symbolTabs.map(sym => (
                <button
                  key={sym}
                  className={`gs-symbol-tab ${symbolTab === sym ? 'active' : ''}`}
                  onClick={() => setSymbolTab(sym)}
                >
                  {sym}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="gs-content">
            {activeSection === 'general' && renderGeneralContent()}
            {activeSection === 'strategy' && (vestSymbol ? <SymbolSettingsPanel symbol={vestSymbol} /> : renderStrategyContent())}
            {activeSection === 'symbols' && <SymbolManagementPanel />}
          </div>

          {/* Footer */}
          {activeSection === 'strategy' && !vestSymbol && (
            <div className="gs-footer">
              <button className="gs-btn gs-btn-reset" onClick={handleReset}>
                Reset
              </button>
              {/* Sync status indicator */}
              {lastSyncAt && !isSyncing && (
                <span className="gs-sync-status">
                  Synced {formatTimeAgo(lastSyncAt)}
                </span>
              )}
              {lastSyncError && (
                <span className="gs-sync-error" title={lastSyncError}>
                  Sync failed
                </span>
              )}
              <div className="gs-footer-spacer" />
              <button className="gs-btn gs-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className={`gs-btn gs-btn-save ${currentHasChanges ? 'has-changes' : ''}`}
                onClick={handleSave}
                disabled={!currentHasChanges || isSyncing}
              >
                {isSyncing ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ATR Optimization Modal */}
      <ATROptimizationModal
        isOpen={showOptimizationModal}
        onClose={() => setShowOptimizationModal(false)}
        onApplyResults={handleApplyOptimizationResults}
      />
    </div>,
    document.body
  );
};

export default GlobalSettingsModal;
